import React from "react";
import { PageHeader } from "../components/PageHeader/PageHeader";
import { useFinanceConfig } from "../context/FinanceConfigContext";
import type { PriceBook, PackageType } from "../context/FinanceConfigContext";
import { useBusinessHours, BusinessHoursMode, WeekSchedule, BUSINESS_DEFAULTS, EXTENDED_DEFAULTS, MODE_LABELS } from "../context/BusinessHoursContext";

// ─── Section Types ────────────────────────────────────────────────────────────

type SettingsSection =
  | "general"
  | "users-teams"
  | "access-permissions"
  | "asset-config"
  | "custom-fields-asset"
  | "import-data-asset"
  | "custom-fields-work"
  | "import-data-work"
  | "resource-management"
  | "time-tracker-tags"
  | "work-order-types"
  | "work-order-statuses"
  | "sla-config"
  | "notifications-work"
  | "email-config"
  | "finance-invoice-config"
  | "finance-branding"
  | "finance-service-packages"
  | "finance-price-book"
  | "finance-package-types"
  | "custom-fields-company"
  | "import-data-company"
  | "branding"
  | "notifications-portal"
  | "profile"
  | "notifications-personal"
  | "security";

// ─── Icons ────────────────────────────────────────────────────────────────────

const IcGeneral = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="10" cy="10" r="2.5" />
    <path d="M10 2v1.5M10 16.5V18M2 10h1.5M16.5 10H18M3.93 3.93l1.06 1.06M14.01 14.01l1.06 1.06M3.93 16.07l1.06-1.06M14.01 5.99l1.06-1.06" />
  </svg>
);

const IcUsers = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="7.5" cy="6.5" r="2.5" />
    <path d="M2 17c0-3 2.5-5 5.5-5s5.5 2 5.5 5" />
    <path d="M13 4.5a2.5 2.5 0 1 1 0 5M18 17c0-2.5-1.5-4.5-4-5" />
  </svg>
);

const IcAccess = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 2L3 5v5c0 4 3 7 7 8 4-1 7-4 7-8V5L10 2z" />
  </svg>
);

const IcAsset = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="10" cy="10" r="2" />
    <circle cx="10" cy="10" r="6.5" />
    <path d="M10 3.5V6M10 14v2.5M3.5 10H6M14 10h2.5" />
  </svg>
);

const IcCustomField = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="14" height="12" rx="2" />
    <path d="M7 8h6M7 12h4" />
  </svg>
);

const IcUpload = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 13V4M6.5 7L10 3.5 13.5 7" />
    <path d="M3 14v1.5A1.5 1.5 0 0 0 4.5 17h11A1.5 1.5 0 0 0 17 15.5V14" />
  </svg>
);

const IcResource = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="7" cy="7" r="3" />
    <circle cx="13" cy="13" r="3" />
    <path d="M9.5 9.5l1 1" />
  </svg>
);

const IcClock = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="10" cy="10" r="7.5" />
    <path d="M10 6v4l2.5 2.5" />
  </svg>
);

const IcWorkOrderType = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="2.5" width="12" height="15" rx="2" />
    <path d="M7 7h6M7 10.5h4M7 14h3" />
  </svg>
);

const IcStatus = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="10" cy="10" r="7.5" />
    <circle cx="10" cy="10" r="3" fill="currentColor" />
  </svg>
);

const IcBell = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 2.5a5.5 5.5 0 0 0-5.5 5.5v3.5L3 13h14l-1.5-1.5V8A5.5 5.5 0 0 0 10 2.5z" />
    <path d="M8.5 13v.5a1.5 1.5 0 0 0 3 0V13" />
  </svg>
);

const IcMail = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2.5" y="4.5" width="15" height="11" rx="2" />
    <path d="M2.5 7l7.5 5 7.5-5" />
  </svg>
);

const IcBranding = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="10" cy="10" r="7.5" />
    <path d="M7 10.5c0-1.5 1-2.5 3-2.5s3 1 3 2.5c0 2-3 4-3 4s-3-2-3-4z" />
  </svg>
);

const IcProfile = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="10" cy="7" r="3" />
    <path d="M3.5 18c0-3.5 3-6 6.5-6s6.5 2.5 6.5 6" />
  </svg>
);

const IcLock = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="9" width="12" height="9" rx="2" />
    <path d="M7 9V6a3 3 0 0 1 6 0v3" />
    <circle cx="10" cy="14" r="1" fill="currentColor" />
  </svg>
);

const IcDisconnect = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8.5 11.5L11.5 8.5" />
    <path d="M6.5 13.5l-1.06 1.06a3 3 0 0 1-4.24-4.24l3-3a3 3 0 0 1 4.12.12" />
    <path d="M13.5 6.5l1.06-1.06a3 3 0 0 1 4.24 4.24l-3 3a3 3 0 0 1-4.12-.12" />
    <path d="M3 3l14 14" />
  </svg>
);

const IcPlus = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
    <path d="M8 3v10M3 8h10" />
  </svg>
);

const IcInvoice = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="2" width="12" height="16" rx="2" />
    <path d="M7 7h6M7 10h4" />
    <path d="M7 13.5h2.5" />
    <circle cx="13" cy="13.5" r="1.5" />
  </svg>
);

const IcFinanceBranding = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="4" width="16" height="12" rx="2" />
    <path d="M2 7h16M2 13h16" />
  </svg>
);

const IcServicePackages = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="6" height="6" rx="1.5" />
    <rect x="11" y="3" width="6" height="6" rx="1.5" />
    <rect x="3" y="11" width="6" height="6" rx="1.5" />
    <path d="M11 14h6M14 11v6" />
  </svg>
);

const IcPriceBook = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 3h9l3 3v11a1 1 0 01-1 1H4a1 1 0 01-1-1V4a1 1 0 011-1z" />
    <path d="M13 3v3h3" />
    <path d="M7 9h6M7 12h4" />
    <circle cx="13.5" cy="13.5" r="2.5" />
    <path d="M13.5 12.5v1l.7.7" />
  </svg>
);

const IcSLA = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="10" cy="10" r="7.5" />
    <path d="M10 5.5v4.5l3 2" />
    <path d="M5 2.5l1.5 1.5M15 2.5l-1.5 1.5" />
  </svg>
);

const IcImage = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--color-content-tertiary)" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="16" rx="2.5" />
    <circle cx="8.5" cy="9.5" r="1.5" />
    <path d="M3 16l4.5-4.5 3.5 3.5 2.5-2.5L21 16" />
  </svg>
);

const IcChevronDown = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round">
    <path d="M4 6l4 4 4-4" />
  </svg>
);

// Toolbar icons for the description editor
const IcBold = () => <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M6 10h5.5a2.5 2.5 0 0 0 0-5H6v5zM6 10h6a2.5 2.5 0 0 1 0 5H6v-5z" /></svg>;
const IcItalic = () => <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M11 5H8M12 15H9M10.5 5l-2 10" /></svg>;
const IcUnderline = () => <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M7 5v5a3 3 0 0 0 6 0V5M5 15h10" /></svg>;
const IcStrike = () => <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M5 10h10" /><path d="M8 7.5C8 6.1 9 5 10.5 5S13 6 13 7.5c0 1-0.6 1.8-1.5 2.5" /><path d="M8 13.5c0 .8.7 1.5 2 1.5s2-.7 2-1.5" /></svg>;
const IcAlignLeft = () => <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M3 6h14M3 10h9M3 14h12" /></svg>;
const IcAlignCenter = () => <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M3 6h14M5 10h10M4 14h12" /></svg>;
const IcAlignRight = () => <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M3 6h14M8 10h9M5 14h12" /></svg>;
const IcBulletList = () => <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><circle cx="4" cy="6" r="1" fill="currentColor" /><path d="M7 6h10" /><circle cx="4" cy="10" r="1" fill="currentColor" /><path d="M7 10h10" /><circle cx="4" cy="14" r="1" fill="currentColor" /><path d="M7 14h10" /></svg>;
const IcNumberList = () => <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M3.5 5h1M3.5 5v3M7 6h10M7 10h10M7 14h10" /><path d="M3.5 10.5c0-.6.4-1 1-1h.5a.5.5 0 0 1 0 1l-1.5 1.5H5" /><path d="M3.5 15.5h1a.5.5 0 0 0 0-1h-.5a.5.5 0 0 1 0-1H5" /></svg>;

// ─── Nav Item ─────────────────────────────────────────────────────────────────

function NavItem({
  icon,
  label,
  active,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  const [hovered, setHovered] = React.useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        width: "100%",
        height: 36,
        padding: "6px 8px",
        borderRadius: 8,
        border: "none",
        cursor: "pointer",
        background: active ? "var(--color-bg-accent)" : hovered ? "var(--color-bg-accent)" : "transparent",
        color: active ? "var(--color-content-primary)" : "var(--color-content-secondary)",
        textAlign: "left",
        transition: "background 80ms ease",
        flexShrink: 0,
      }}
    >
      <span style={{ display: "flex", alignItems: "center", flexShrink: 0, opacity: active ? 1 : 0.8 }}>
        {icon}
      </span>
      <span style={{
        fontFamily: "var(--font-family-base)",
        fontSize: 14,
        fontWeight: active ? 700 : 500,
        lineHeight: "20px",
        letterSpacing: active ? "-0.28px" : 0,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        flex: 1,
        minWidth: 0,
      }}>
        {label}
      </span>
    </button>
  );
}

// ─── Nav Group ────────────────────────────────────────────────────────────────

function NavGroup({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, width: "100%" }}>
      <span style={{
        fontFamily: "var(--font-family-base)",
        fontSize: 12,
        fontWeight: 500,
        color: "var(--color-content-secondary)",
        lineHeight: "20px",
        paddingLeft: 4,
      }}>
        {title}
      </span>
      <div style={{ display: "flex", flexDirection: "column", gap: 2, paddingLeft: 8 }}>
        {children}
      </div>
    </div>
  );
}

// ─── Field Label ──────────────────────────────────────────────────────────────

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <span style={{
      fontFamily: "var(--font-family-base)",
      fontSize: 12,
      fontWeight: 500,
      color: "var(--color-content-secondary)",
      lineHeight: "16px",
    }}>
      {children}
    </span>
  );
}

// ─── Text Input ───────────────────────────────────────────────────────────────

function TextInput({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={{
        width: "100%",
        height: 40,
        padding: "0 12px",
        border: "1px solid var(--color-border-primary)",
        borderRadius: 8,
        background: "var(--color-bg-primary)",
        fontFamily: "var(--font-family-base)",
        fontSize: 12,
        fontWeight: 500,
        color: "var(--color-content-primary)",
        outline: "none",
        boxSizing: "border-box",
      }}
    />
  );
}

// ─── Form Field ───────────────────────────────────────────────────────────────

function FormField({
  label,
  children,
  hint,
  style,
}: {
  label: string;
  children: React.ReactNode;
  hint?: string;
  style?: React.CSSProperties;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, ...style }}>
      <FieldLabel>{label}</FieldLabel>
      {children}
      {hint && (
        <span style={{
          fontFamily: "var(--font-family-base)",
          fontSize: 12,
          fontWeight: 500,
          color: "var(--color-content-tertiary)",
          lineHeight: "16px",
        }}>
          {hint}
        </span>
      )}
    </div>
  );
}

// ─── Select ───────────────────────────────────────────────────────────────────

function SelectInput({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <div style={{ position: "relative" }}>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: "100%",
          height: 40,
          padding: "0 32px 0 12px",
          border: "1px solid var(--color-border-primary)",
          borderRadius: 8,
          background: "var(--color-bg-primary)",
          fontFamily: "var(--font-family-base)",
          fontSize: 12,
          fontWeight: 500,
          color: "var(--color-content-primary)",
          outline: "none",
          appearance: "none",
          cursor: "pointer",
          boxSizing: "border-box",
        }}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      <div style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", color: "var(--color-content-secondary)" }}>
        <IcChevronDown />
      </div>
    </div>
  );
}

// ─── Description Editor ───────────────────────────────────────────────────────

function DescriptionEditor({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const toolbarBtn: React.CSSProperties = {
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "var(--color-content-secondary)",
    borderRadius: 4,
    width: 28,
    height: 28,
    flexShrink: 0,
  };

  return (
    <div style={{
      border: "1px solid var(--color-border-primary)",
      borderRadius: 8,
      background: "var(--color-bg-primary)",
      overflow: "hidden",
    }}>
      {/* Toolbar */}
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        padding: "8px 12px",
        borderBottom: "1px solid var(--color-border-primary)",
      }}>
        <button style={toolbarBtn} title="Numbered list"><IcNumberList /></button>
        <button style={toolbarBtn} title="Bullet list"><IcBulletList /></button>
        <div style={{ width: 1, height: 16, background: "var(--color-border-primary)", margin: "0 4px" }} />
        <button style={toolbarBtn} title="Align left"><IcAlignLeft /></button>
        <button style={toolbarBtn} title="Align center"><IcAlignCenter /></button>
        <button style={toolbarBtn} title="Align right"><IcAlignRight /></button>
        <div style={{ width: 1, height: 16, background: "var(--color-border-primary)", margin: "0 4px" }} />
        <button style={toolbarBtn} title="Strikethrough"><IcStrike /></button>
        <button style={toolbarBtn} title="Italic"><IcItalic /></button>
        <button style={toolbarBtn} title="Bold"><IcBold /></button>
        <button style={toolbarBtn} title="Underline"><IcUnderline /></button>
      </div>
      {/* Text area */}
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={6}
        style={{
          width: "100%",
          padding: "12px 16px",
          border: "none",
          outline: "none",
          resize: "vertical",
          fontFamily: "var(--font-family-base)",
          fontSize: 12,
          fontWeight: 500,
          color: "var(--color-content-primary)",
          lineHeight: "20px",
          boxSizing: "border-box",
          background: "transparent",
          minHeight: 120,
        }}
      />
    </div>
  );
}

// ─── Add Email Modal ──────────────────────────────────────────────────────────

function AddEmailModal({
  onClose,
  onAdd,
}: {
  onClose: () => void;
  onAdd: (email: string, shared: boolean) => void;
}) {
  const [email, setEmail] = React.useState("");
  const [shared, setShared] = React.useState(false);
  const [emailError, setEmailError] = React.useState("");

  function handleConnect() {
    if (!email.trim()) {
      setEmailError("Please enter an email address.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setEmailError("Please enter a valid email address.");
      return;
    }
    onAdd(email.trim(), shared);
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.32)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 480,
          background: "var(--color-bg-primary)",
          borderRadius: 12,
          boxShadow: "0 8px 32px rgba(0,0,0,0.16)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Modal header */}
        <div style={{
          padding: "20px 24px 16px",
          borderBottom: "1px solid var(--color-border-primary)",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 16,
        }}>
          <div>
            <h3 style={{
              margin: "0 0 4px",
              fontFamily: "var(--font-family-base)",
              fontSize: 16,
              fontWeight: 700,
              color: "var(--color-content-primary)",
              letterSpacing: "-0.32px",
              lineHeight: "24px",
            }}>
              Connect Outlook Email
            </h3>
            <p style={{
              margin: 0,
              fontFamily: "var(--font-family-base)",
              fontSize: 14,
              fontWeight: 500,
              color: "var(--color-content-secondary)",
              lineHeight: "20px",
            }}>
              Connect your Microsoft Outlook account to receive and send emails directly from Work Orders.
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              flexShrink: 0,
              width: 28,
              height: 28,
              border: "none",
              background: "transparent",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "var(--color-content-secondary)",
              borderRadius: 6,
              padding: 0,
            }}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
              <path d="M4 4l8 8M12 4l-8 8" />
            </svg>
          </button>
        </div>

        {/* Modal body */}
        <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 20 }}>

          {/* Outlook branding strip */}
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "12px 14px",
            border: "1px solid var(--color-border-primary)",
            borderRadius: 8,
            background: "var(--color-bg-accent)",
          }}>
            {/* Microsoft Outlook icon (simplified) */}
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
              <rect width="28" height="28" rx="4" fill="#0078D4" />
              <path d="M14 7C10.13 7 7 10.13 7 14s3.13 7 7 7 7-3.13 7-7-3.13-7-7-7z" fill="white" opacity="0.2" />
              <path d="M8 10h12v1.5L14 15 8 11.5V10z" fill="white" />
              <path d="M8 12.5V19h12v-6.5L14 16.5 8 12.5z" fill="white" />
            </svg>
            <div>
              <p style={{ margin: 0, fontFamily: "var(--font-family-base)", fontSize: 13, fontWeight: 700, color: "var(--color-content-primary)", lineHeight: "18px" }}>
                Microsoft Outlook
              </p>
              <p style={{ margin: 0, fontFamily: "var(--font-family-base)", fontSize: 12, fontWeight: 500, color: "var(--color-content-secondary)", lineHeight: "16px" }}>
                Sign in with your Microsoft account to connect
              </p>
            </div>
          </div>

          {/* Email input */}
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <FieldLabel>Email Address</FieldLabel>
            <input
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setEmailError(""); }}
              placeholder="e.g. name@company.com"
              style={{
                width: "100%",
                height: 40,
                padding: "0 12px",
                border: `1px solid ${emailError ? "var(--color-status-danger, #e5343d)" : "var(--color-border-primary)"}`,
                borderRadius: 8,
                background: "var(--color-bg-primary)",
                fontFamily: "var(--font-family-base)",
                fontSize: 12,
                fontWeight: 500,
                color: "var(--color-content-primary)",
                outline: "none",
                boxSizing: "border-box",
              }}
            />
            {emailError && (
              <span style={{ fontFamily: "var(--font-family-base)", fontSize: 12, fontWeight: 500, color: "var(--color-status-danger, #e5343d)", lineHeight: "16px" }}>
                {emailError}
              </span>
            )}
          </div>

          {/* Shared checkbox */}
          <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer" }}>
            <div
              onClick={() => setShared((s) => !s)}
              style={{
                width: 16,
                height: 16,
                flexShrink: 0,
                marginTop: 2,
                border: `1.5px solid ${shared ? "var(--color-bg-brand-dark)" : "var(--color-border-primary)"}`,
                borderRadius: 4,
                background: shared ? "var(--color-bg-brand-dark)" : "var(--color-bg-primary)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                transition: "background 100ms ease, border-color 100ms ease",
              }}
            >
              {shared && (
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M2 5l2.5 2.5L8 2.5" />
                </svg>
              )}
            </div>
            <div onClick={() => setShared((s) => !s)}>
              <p style={{ margin: 0, fontFamily: "var(--font-family-base)", fontSize: 13, fontWeight: 600, color: "var(--color-content-primary)", lineHeight: "18px" }}>
                Allow other users to see all emails
              </p>
              <p style={{ margin: "2px 0 0", fontFamily: "var(--font-family-base)", fontSize: 12, fontWeight: 500, color: "var(--color-content-secondary)", lineHeight: "16px" }}>
                Other users will be able to view all emails that have been connected to Work Orders from this account.
              </p>
            </div>
          </label>
        </div>

        {/* Modal footer */}
        <div style={{
          padding: "12px 24px 20px",
          display: "flex",
          justifyContent: "flex-end",
          gap: 8,
        }}>
          <button
            onClick={onClose}
            style={{
              height: 36,
              padding: "0 16px",
              border: "1px solid var(--color-border-primary)",
              borderRadius: 8,
              background: "var(--color-bg-primary)",
              fontFamily: "var(--font-family-base)",
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: "-0.24px",
              color: "var(--color-content-secondary)",
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleConnect}
            style={{
              height: 36,
              padding: "0 16px",
              border: "none",
              borderRadius: 8,
              background: "var(--color-bg-brand-dark)",
              fontFamily: "var(--font-family-base)",
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: "-0.24px",
              color: "var(--color-content-inverse)",
              cursor: "pointer",
            }}
          >
            Connect with Outlook
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Email Config Content ─────────────────────────────────────────────────────

type ConnectedEmail = { id: string; address: string; shared: boolean };

function EmailConfigContent() {
  const [emails, setEmails] = React.useState<ConnectedEmail[]>([
    { id: "1", address: "micheal@company.com", shared: false },
  ]);
  const [showModal, setShowModal] = React.useState(false);

  function handleAdd(address: string, shared: boolean) {
    setEmails((prev) => [...prev, { id: Date.now().toString(), address, shared }]);
    setShowModal(false);
  }

  function handleDisconnect(id: string) {
    setEmails((prev) => prev.filter((e) => e.id !== id));
  }

  return (
    <>
      <div style={{ flex: 1, overflowY: "auto", padding: "24px 32px 48px" }}>
        <div style={{ maxWidth: 880, display: "flex", flexDirection: "column", gap: 24 }}>

          {/* Section header */}
          <div>
            <h2 style={{ margin: "0 0 6px", fontSize: 16, fontWeight: 700, color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)", letterSpacing: "-0.32px", lineHeight: "24px" }}>
              Email Configuration
            </h2>
            <p style={{ margin: 0, fontSize: 14, fontWeight: 500, color: "var(--color-content-secondary)", fontFamily: "var(--font-family-base)", lineHeight: "20px" }}>
              Configure your email address to access emails from your account
            </p>
          </div>

          {/* Add Email link button */}
          <button
            onClick={() => setShowModal(true)}
            style={{
              alignSelf: "flex-start",
              display: "flex",
              alignItems: "center",
              gap: 4,
              padding: "4px 4px",
              border: "none",
              background: "transparent",
              cursor: "pointer",
              color: "var(--color-bg-brand-dark)",
              fontFamily: "var(--font-family-base)",
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: "-0.24px",
              lineHeight: "16px",
            }}
          >
            <IcPlus />
            Add Email
          </button>

          {/* Connected emails list */}
          {emails.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {emails.map((e) => (
                <div
                  key={e.id}
                  style={{
                    width: 415,
                    display: "flex",
                    alignItems: "center",
                    gap: 16,
                    padding: 16,
                    border: "1px solid var(--color-border-primary)",
                    borderRadius: 8,
                    background: "var(--color-bg-primary)",
                  }}
                >
                  <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                    <IcMail />
                    <span style={{
                      fontFamily: "var(--font-family-base)",
                      fontSize: 14,
                      fontWeight: 500,
                      color: "var(--color-content-primary)",
                      lineHeight: "20px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}>
                      {e.address}
                    </span>
                    {e.shared && (
                      <span style={{
                        flexShrink: 0,
                        padding: "1px 6px",
                        borderRadius: 4,
                        background: "var(--color-bg-accent)",
                        fontFamily: "var(--font-family-base)",
                        fontSize: 11,
                        fontWeight: 600,
                        color: "var(--color-content-secondary)",
                        lineHeight: "16px",
                      }}>
                        Shared
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => handleDisconnect(e.id)}
                    title="Disconnect"
                    style={{
                      flexShrink: 0,
                      border: "none",
                      background: "transparent",
                      cursor: "pointer",
                      padding: 0,
                      display: "flex",
                      alignItems: "center",
                      color: "var(--color-content-secondary)",
                    }}
                  >
                    <IcDisconnect />
                  </button>
                </div>
              ))}
            </div>
          )}

        </div>
      </div>

      {showModal && (
        <AddEmailModal onClose={() => setShowModal(false)} onAdd={handleAdd} />
      )}
    </>
  );
}

// ─── Shared: Save Button ──────────────────────────────────────────────────────

function SaveButton({ onClick }: { onClick?: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        height: 32,
        padding: "0 14px",
        border: "none",
        borderRadius: 8,
        background: "var(--color-bg-brand-dark)",
        fontFamily: "var(--font-family-base)",
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: "-0.24px",
        color: "var(--color-content-inverse)",
        cursor: "pointer",
        flexShrink: 0,
      }}
    >
      Save
    </button>
  );
}

// ─── Shared: Image Upload Field ───────────────────────────────────────────────

function ImageUploadField({
  label,
  hint,
  preview,
  onUpload,
  onRemove,
  aspectLabel,
}: {
  label: string;
  hint: string;
  preview: string | null;
  onUpload: (src: string) => void;
  onRemove: () => void;
  aspectLabel: string;
}) {
  const inputRef = React.useRef<HTMLInputElement>(null);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => onUpload(ev.target?.result as string);
    reader.readAsDataURL(file);
  }

  return (
    <FormField label={label} hint={hint}>
      <div
        onClick={() => !preview && inputRef.current?.click()}
        style={{
          width: "100%",
          maxWidth: 600,
          height: 160,
          border: `1.5px dashed ${preview ? "var(--color-border-primary)" : "var(--color-border-primary)"}`,
          borderRadius: 10,
          background: preview ? "transparent" : "var(--color-bg-accent)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: preview ? "default" : "pointer",
          overflow: "hidden",
          position: "relative",
          transition: "background 100ms ease",
        }}
      >
        {preview ? (
          <>
            <img
              src={preview}
              alt={label}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
            <button
              onClick={(e) => { e.stopPropagation(); onRemove(); }}
              style={{
                position: "absolute",
                top: 8,
                right: 8,
                width: 28,
                height: 28,
                borderRadius: 6,
                border: "none",
                background: "rgba(0,0,0,0.45)",
                color: "white",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="white" strokeWidth="1.6" strokeLinecap="round">
                <path d="M3 3l8 8M11 3l-8 8" />
              </svg>
            </button>
          </>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, pointerEvents: "none" }}>
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none" stroke="var(--color-content-tertiary)" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="5" width="22" height="18" rx="3" />
              <circle cx="9.5" cy="11.5" r="2" />
              <path d="M3 19l6-5.5 4 4 3-3L25 19" />
            </svg>
            <span style={{ fontFamily: "var(--font-family-base)", fontSize: 12, fontWeight: 600, color: "var(--color-content-secondary)" }}>
              Click to upload {aspectLabel}
            </span>
          </div>
        )}
      </div>
      {!preview && (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button
            onClick={() => inputRef.current?.click()}
            style={{
              alignSelf: "flex-start",
              height: 32,
              padding: "0 14px",
              border: "1px solid var(--color-border-primary)",
              borderRadius: 8,
              background: "var(--color-bg-primary)",
              fontFamily: "var(--font-family-base)",
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: "-0.24px",
              color: "var(--color-content-secondary)",
              cursor: "pointer",
            }}
          >
            Upload image
          </button>
        </div>
      )}
      {preview && (
        <button
          onClick={() => inputRef.current?.click()}
          style={{
            alignSelf: "flex-start",
            height: 32,
            padding: "0 14px",
            border: "1px solid var(--color-border-primary)",
            borderRadius: 8,
            background: "var(--color-bg-primary)",
            fontFamily: "var(--font-family-base)",
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: "-0.24px",
            color: "var(--color-content-secondary)",
            cursor: "pointer",
          }}
        >
          Replace image
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg"
        style={{ display: "none" }}
        onChange={handleChange}
      />
    </FormField>
  );
}

// ─── Finance: Invoice & Quotation Config ──────────────────────────────────────

function FinanceInvoiceConfigContent() {
  const { config, setConfig } = useFinanceConfig();

  const [senderName, setSenderName] = React.useState(config.senderName);
  const [street, setStreet] = React.useState(config.street);
  const [city, setCity] = React.useState(config.city);
  const [postalCode, setPostalCode] = React.useState(config.postalCode);
  const [country, setCountry] = React.useState(config.country);
  const [vatId, setVatId] = React.useState(config.vatId);
  const [taxNumber, setTaxNumber] = React.useState(config.taxNumber);
  const [iban, setIban] = React.useState(config.iban);
  const [bic, setBic] = React.useState(config.bic);
  const [paymentTerms, setPaymentTerms] = React.useState(config.paymentTerms);
  const [currency, setCurrency] = React.useState(config.currency);
  const [invoicePrefix, setInvoicePrefix] = React.useState(config.invoicePrefix);
  const [quotationPrefix, setQuotationPrefix] = React.useState(config.quotationPrefix);
  const [invoiceNotes, setInvoiceNotes] = React.useState(config.invoiceNotes);
  const [quotationNotes, setQuotationNotes] = React.useState(config.quotationNotes);
  const [defaultMargin, setDefaultMargin] = React.useState(config.defaultMargin ?? 0);

  function handleSave() {
    setConfig({ senderName, street, city, postalCode, country, vatId, taxNumber, iban, bic, paymentTerms, currency, invoicePrefix, quotationPrefix, invoiceNotes, quotationNotes, defaultMargin });
  }

  const paymentTermsOptions = [
    { value: "0", label: "Due immediately" },
    { value: "14", label: "Net 14 days" },
    { value: "30", label: "Net 30 days" },
    { value: "60", label: "Net 60 days" },
    { value: "90", label: "Net 90 days" },
  ];

  const currencyOptions = [
    { value: "EUR", label: "EUR — Euro (€)" },
    { value: "USD", label: "USD — US Dollar ($)" },
    { value: "GBP", label: "GBP — British Pound (£)" },
    { value: "CHF", label: "CHF — Swiss Franc (Fr.)" },
    { value: "AED", label: "AED — UAE Dirham (د.إ)" },
    { value: "AUD", label: "AUD — Australian Dollar (A$)" },
    { value: "CAD", label: "CAD — Canadian Dollar (C$)" },
    { value: "CNY", label: "CNY — Chinese Yuan (¥)" },
    { value: "DKK", label: "DKK — Danish Krone (kr)" },
    { value: "HKD", label: "HKD — Hong Kong Dollar (HK$)" },
    { value: "INR", label: "INR — Indian Rupee (₹)" },
    { value: "JPY", label: "JPY — Japanese Yen (¥)" },
    { value: "KWD", label: "KWD — Kuwaiti Dinar (KD)" },
    { value: "MXN", label: "MXN — Mexican Peso (MX$)" },
    { value: "NOK", label: "NOK — Norwegian Krone (kr)" },
    { value: "NZD", label: "NZD — New Zealand Dollar (NZ$)" },
    { value: "PLN", label: "PLN — Polish Zloty (zł)" },
    { value: "SAR", label: "SAR — Saudi Riyal (SR)" },
    { value: "SEK", label: "SEK — Swedish Krona (kr)" },
    { value: "SGD", label: "SGD — Singapore Dollar (S$)" },
    { value: "TRY", label: "TRY — Turkish Lira (₺)" },
    { value: "ZAR", label: "ZAR — South African Rand (R)" },
  ];

  const divider = <div style={{ height: 1, background: "var(--color-border-primary)" }} />;

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: "24px 32px 48px" }}>
      <div style={{ maxWidth: 880, display: "flex", flexDirection: "column", gap: 32 }}>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16 }}>
          <div>
            <h2 style={{ margin: "0 0 6px", fontSize: 16, fontWeight: 700, color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)", letterSpacing: "-0.32px", lineHeight: "24px" }}>
              Quotation & Invoice Configuration
            </h2>
            <p style={{ margin: 0, fontSize: 14, fontWeight: 500, color: "var(--color-content-secondary)", fontFamily: "var(--font-family-base)", lineHeight: "20px" }}>
              Configure sender details, tax information, and defaults used on all quotations and invoices.
            </p>
          </div>
          <SaveButton onClick={handleSave} />
        </div>

        {divider}

        {/* ── Sender Details ─────────────────────────────────────────────── */}
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <h3 style={{ margin: "0 0 16px", fontSize: 13, fontWeight: 700, color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)", lineHeight: "20px", textTransform: "uppercase", letterSpacing: "0.04em" }}>
            Sender Details
          </h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <FormField label="Company / Sender Name *" style={{ maxWidth: 415 }}>
              <TextInput value={senderName} onChange={setSenderName} placeholder="e.g. Acme Technologies GmbH" />
            </FormField>
            <FormField label="Street Address *" style={{ maxWidth: 415 }}>
              <TextInput value={street} onChange={setStreet} placeholder="e.g. Berliner Str. 42" />
            </FormField>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, maxWidth: 415 }}>
              <FormField label="Postal Code *">
                <TextInput value={postalCode} onChange={setPostalCode} placeholder="e.g. 10115" />
              </FormField>
              <FormField label="City *">
                <TextInput value={city} onChange={setCity} placeholder="e.g. Berlin" />
              </FormField>
            </div>
            <FormField label="Country *" style={{ maxWidth: 415 }}>
              <TextInput value={country} onChange={setCountry} placeholder="e.g. Germany" />
            </FormField>
          </div>
        </div>

        {divider}

        {/* ── Tax & Banking ──────────────────────────────────────────────── */}
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <h3 style={{ margin: "0 0 16px", fontSize: 13, fontWeight: 700, color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)", textTransform: "uppercase", letterSpacing: "0.04em", lineHeight: "20px" }}>
            Tax & Banking
          </h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, maxWidth: 846 }}>
              <FormField label="VAT ID *">
                <TextInput value={vatId} onChange={setVatId} placeholder="e.g. DE123456789" />
              </FormField>
              <FormField label="Tax Number">
                <TextInput value={taxNumber} onChange={setTaxNumber} placeholder="e.g. 30/123/12345" />
              </FormField>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, maxWidth: 846 }}>
              <FormField label="IBAN">
                <TextInput value={iban} onChange={setIban} placeholder="e.g. DE89 3704 0044 0532 0130 00" />
              </FormField>
              <FormField label="BIC / SWIFT">
                <TextInput value={bic} onChange={setBic} placeholder="e.g. COBADEFFXXX" />
              </FormField>
            </div>
          </div>
        </div>

        {divider}

        {/* ── Document Defaults ──────────────────────────────────────────── */}
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <h3 style={{ margin: "0 0 16px", fontSize: 13, fontWeight: 700, color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)", textTransform: "uppercase", letterSpacing: "0.04em", lineHeight: "20px" }}>
            Document Defaults
          </h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, maxWidth: 846 }}>
              <FormField label="Payment Terms">
                <SelectInput value={paymentTerms} onChange={setPaymentTerms} options={paymentTermsOptions} />
              </FormField>
              <FormField label="Currency" hint="Used on all quotations and invoices created in this workspace.">
                <SelectInput value={currency} onChange={setCurrency} options={currencyOptions} />
              </FormField>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, maxWidth: 846 }}>
              <FormField label="Invoice Number Prefix" hint="Applied before the auto-incremented invoice number.">
                <TextInput value={invoicePrefix} onChange={setInvoicePrefix} placeholder="e.g. INV-" />
              </FormField>
              <FormField label="Quotation Number Prefix" hint="Applied before the auto-incremented quotation number.">
                <TextInput value={quotationPrefix} onChange={setQuotationPrefix} placeholder="e.g. QUO-" />
              </FormField>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, maxWidth: 846 }}>
              <FormField label="Default Margin (%)" hint="Pre-filled on every new line item in quotations and invoices. Can be overridden per line.">
                <div style={{ position: "relative" }}>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    step={0.1}
                    value={defaultMargin}
                    onChange={e => setDefaultMargin(Number(e.target.value))}
                    style={{
                      width: "100%",
                      height: 38,
                      border: "1px solid var(--color-border-primary)",
                      borderRadius: 8,
                      padding: "0 32px 0 12px",
                      fontSize: 13,
                      fontWeight: 500,
                      fontFamily: "var(--font-family-base)",
                      background: "var(--color-bg-primary)",
                      color: "var(--color-content-primary)",
                      outline: "none",
                      boxSizing: "border-box",
                    }}
                  />
                  <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", fontSize: 13, color: "var(--color-content-secondary)", fontFamily: "var(--font-family-base)", pointerEvents: "none" }}>%</span>
                </div>
              </FormField>
            </div>
            <FormField label="Invoice Footer Notes" hint="Shown in the footer of every invoice. Useful for payment instructions or legal notices.">
              <textarea
                value={invoiceNotes}
                onChange={(e) => setInvoiceNotes(e.target.value)}
                placeholder="e.g. Please transfer the amount within the due date to the bank account above."
                rows={4}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: "1px solid var(--color-border-primary)",
                  borderRadius: 8,
                  background: "var(--color-bg-primary)",
                  fontFamily: "var(--font-family-base)",
                  fontSize: 12,
                  fontWeight: 500,
                  color: "var(--color-content-primary)",
                  lineHeight: "20px",
                  resize: "vertical",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
            </FormField>
            <FormField label="Quotation Footer Notes" hint="Shown in the footer of every quotation. Useful for validity periods or terms.">
              <textarea
                value={quotationNotes}
                onChange={(e) => setQuotationNotes(e.target.value)}
                placeholder="e.g. This quotation is valid for 30 days from the date of issue."
                rows={4}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: "1px solid var(--color-border-primary)",
                  borderRadius: 8,
                  background: "var(--color-bg-primary)",
                  fontFamily: "var(--font-family-base)",
                  fontSize: 12,
                  fontWeight: 500,
                  color: "var(--color-content-primary)",
                  lineHeight: "20px",
                  resize: "vertical",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
            </FormField>
          </div>
        </div>

      </div>
    </div>
  );
}

// ─── Finance: Branding ────────────────────────────────────────────────────────

function FinanceBrandingContent() {
  const { config, setConfig } = useFinanceConfig();

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: "24px 32px 48px" }}>
      <div style={{ maxWidth: 880, display: "flex", flexDirection: "column", gap: 32 }}>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16 }}>
          <div>
            <h2 style={{ margin: "0 0 6px", fontSize: 16, fontWeight: 700, color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)", letterSpacing: "-0.32px", lineHeight: "24px" }}>
              Finance Branding
            </h2>
            <p style={{ margin: 0, fontSize: 14, fontWeight: 500, color: "var(--color-content-secondary)", fontFamily: "var(--font-family-base)", lineHeight: "20px" }}>
              Customise the header and footer images printed on your quotations and invoices.
            </p>
          </div>
          <SaveButton />
        </div>

        <div style={{ height: 1, background: "var(--color-border-primary)" }} />

        {/* Fields */}
        <div style={{ display: "flex", flexDirection: "column", gap: 32 }}>
          <ImageUploadField
            label="Header Image"
            hint="Displayed at the top of every quotation and invoice. Recommended size: 1200 × 200 px, PNG or JPEG, max 2 MB."
            preview={config.headerImage}
            onUpload={v => setConfig({ ...config, headerImage: v })}
            onRemove={() => setConfig({ ...config, headerImage: null })}
            aspectLabel="header image (1200 × 200)"
          />
          <div style={{ height: 1, background: "var(--color-border-primary)" }} />
          <ImageUploadField
            label="Footer Image"
            hint="Displayed at the bottom of every quotation and invoice. Recommended size: 1200 × 200 px, PNG or JPEG, max 2 MB."
            preview={config.footerImage}
            onUpload={v => setConfig({ ...config, footerImage: v })}
            onRemove={() => setConfig({ ...config, footerImage: null })}
            aspectLabel="footer image (1200 × 200)"
          />
        </div>

      </div>
    </div>
  );
}

// ─── Placeholder Content ──────────────────────────────────────────────────────

function PlaceholderContent({ title, description }: { title: string; description: string }) {
  return (
    <div style={{
      flex: 1,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 12,
      color: "var(--color-content-tertiary)",
      padding: 48,
    }}>
      <div style={{ width: 48, height: 48, borderRadius: 12, background: "var(--color-bg-accent)", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--color-content-tertiary)" strokeWidth="1.4" strokeLinecap="round"><rect x="4" y="4" width="16" height="16" rx="3" /><path d="M9 12h6M9 8h6M9 16h3" /></svg>
      </div>
      <div style={{ textAlign: "center" }}>
        <p style={{ margin: "0 0 4px", fontSize: 15, fontWeight: 700, color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)" }}>{title}</p>
        <p style={{ margin: 0, fontSize: 13, fontWeight: 500, color: "var(--color-content-secondary)", fontFamily: "var(--font-family-base)", maxWidth: 360, lineHeight: "20px" }}>{description}</p>
      </div>
      <span style={{ fontSize: 12, fontWeight: 600, color: "var(--color-content-tertiary)", fontFamily: "var(--font-family-base)", padding: "4px 12px", border: "1px solid var(--color-border-primary)", borderRadius: 6 }}>
        Coming soon
      </span>
    </div>
  );
}

// ─── Business Hours Section ───────────────────────────────────────────────────

const DAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;
type DayKey = typeof DAY_KEYS[number];
const DAY_LABELS: Record<DayKey, string> = { mon: "Monday", tue: "Tuesday", wed: "Wednesday", thu: "Thursday", fri: "Friday", sat: "Saturday", sun: "Sunday" };

const MODE_META: Record<BusinessHoursMode, { icon: React.ReactNode; title: string; description: string }> = {
  business: {
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="10" cy="10" r="7.5" />
        <path d="M10 6v4l2.5 2.5" />
        <path d="M5.5 3.5l-.5-.5M14.5 3.5l.5-.5" />
      </svg>
    ),
    title: "Business Hours",
    description: "SLA timers only count during your configured working hours.",
  },
  extended: {
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="10" cy="10" r="7.5" />
        <path d="M10 5v5l3 3" />
        <path d="M16 3l1 1M17 7h1M16 11l1 1" />
      </svg>
    ),
    title: "Extended Business Hours",
    description: "Wider coverage — SLA timers run during extended scheduled hours.",
  },
  "24x7": {
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="10" cy="10" r="7.5" />
        <path d="M2.5 10h15M10 2.5a12 12 0 0 1 0 15M10 2.5a12 12 0 0 0 0 15" />
      </svg>
    ),
    title: "Around the Clock",
    description: "SLA timers always run — 24 hours a day, 7 days a week.",
  },
};

function BusinessHoursSection() {
  const { config, setConfig } = useBusinessHours();
  const [mode, setMode] = React.useState<BusinessHoursMode>(config.mode);
  const [schedule, setSchedule] = React.useState<WeekSchedule>(config.schedule);

  function handleModeChange(m: BusinessHoursMode) {
    setMode(m);
    if (m === "business") setSchedule(BUSINESS_DEFAULTS);
    else if (m === "extended") setSchedule(EXTENDED_DEFAULTS);
  }

  function patchDay(day: DayKey, patch: Partial<WeekSchedule[DayKey]>) {
    setSchedule((prev) => ({ ...prev, [day]: { ...prev[day], ...patch } }));
  }

  function handleSave() {
    setConfig({ mode, schedule });
  }

  const showSchedule = mode !== "24x7";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>

      {/* Sub-header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16 }}>
        <div>
          <h2 style={{ margin: "0 0 6px", fontSize: 16, fontWeight: 700, color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)", letterSpacing: "-0.32px", lineHeight: "24px" }}>
            Business Hours
          </h2>
          <p style={{ margin: 0, fontSize: 14, fontWeight: 500, color: "var(--color-content-secondary)", fontFamily: "var(--font-family-base)", lineHeight: "20px" }}>
            Defines when SLA timers run for Work Orders, Tickets, and Contracts.
          </p>
        </div>
        <SaveButton onClick={handleSave} />
      </div>

      {/* Mode cards */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, maxWidth: 846 }}>
        {(["business", "extended", "24x7"] as BusinessHoursMode[]).map((m) => {
          const meta = MODE_META[m];
          const active = mode === m;
          return (
            <button
              key={m}
              onClick={() => handleModeChange(m)}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: 10,
                padding: "16px",
                border: `1.5px solid ${active ? "var(--color-bg-brand-dark)" : "var(--color-border-primary)"}`,
                borderRadius: 10,
                background: active ? "color-mix(in srgb, var(--color-bg-brand-dark) 6%, var(--color-bg-primary))" : "var(--color-bg-primary)",
                cursor: "pointer",
                textAlign: "left",
                transition: "border-color 120ms ease, background 120ms ease",
              }}
            >
              <div style={{
                width: 36,
                height: 36,
                borderRadius: 8,
                background: active ? "color-mix(in srgb, var(--color-bg-brand-dark) 12%, var(--color-bg-primary))" : "var(--color-bg-accent)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: active ? "var(--color-bg-brand-dark)" : "var(--color-content-secondary)",
                flexShrink: 0,
              }}>
                {meta.icon}
              </div>
              <div>
                <div style={{ fontFamily: "var(--font-family-base)", fontSize: 13, fontWeight: 700, color: active ? "var(--color-bg-brand-dark)" : "var(--color-content-primary)", lineHeight: "18px", marginBottom: 4 }}>
                  {meta.title}
                </div>
                <div style={{ fontFamily: "var(--font-family-base)", fontSize: 12, fontWeight: 500, color: "var(--color-content-secondary)", lineHeight: "16px" }}>
                  {meta.description}
                </div>
              </div>
              {/* Active dot */}
              <div style={{ marginTop: "auto", alignSelf: "flex-end", width: 14, height: 14, borderRadius: "50%", border: `2px solid ${active ? "var(--color-bg-brand-dark)" : "var(--color-border-primary)"}`, background: active ? "var(--color-bg-brand-dark)" : "transparent", transition: "background 120ms ease" }} />
            </button>
          );
        })}
      </div>

      {/* Schedule editor */}
      {showSchedule && (
        <div style={{ maxWidth: 560 }}>
          <div style={{
            border: "1px solid var(--color-border-primary)",
            borderRadius: 10,
            overflow: "hidden",
          }}>
            {/* Table header */}
            <div style={{ display: "grid", gridTemplateColumns: "140px 40px 1fr", gap: 0, background: "var(--color-bg-accent)", padding: "8px 16px", borderBottom: "1px solid var(--color-border-primary)" }}>
              <span style={{ fontFamily: "var(--font-family-base)", fontSize: 11, fontWeight: 700, color: "var(--color-content-secondary)", textTransform: "uppercase", letterSpacing: "0.04em" }}>Day</span>
              <span />
              <span style={{ fontFamily: "var(--font-family-base)", fontSize: 11, fontWeight: 700, color: "var(--color-content-secondary)", textTransform: "uppercase", letterSpacing: "0.04em" }}>Hours</span>
            </div>

            {DAY_KEYS.map((day, i) => {
              const d = schedule[day];
              const isLast = i === DAY_KEYS.length - 1;
              return (
                <div
                  key={day}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "140px 40px 1fr",
                    alignItems: "center",
                    padding: "10px 16px",
                    borderBottom: isLast ? "none" : "1px solid var(--color-border-primary)",
                    background: d.enabled ? "var(--color-bg-primary)" : "var(--color-bg-accent)",
                    transition: "background 100ms ease",
                    gap: 0,
                  }}
                >
                  {/* Day name */}
                  <span style={{
                    fontFamily: "var(--font-family-base)",
                    fontSize: 13,
                    fontWeight: 600,
                    color: d.enabled ? "var(--color-content-primary)" : "var(--color-content-tertiary)",
                    lineHeight: "20px",
                  }}>
                    {DAY_LABELS[day]}
                  </span>

                  {/* Toggle */}
                  <div
                    onClick={() => patchDay(day, { enabled: !d.enabled })}
                    style={{
                      width: 28,
                      height: 16,
                      borderRadius: 8,
                      background: d.enabled ? "var(--color-bg-brand-dark)" : "var(--color-border-primary)",
                      cursor: "pointer",
                      position: "relative",
                      flexShrink: 0,
                      transition: "background 120ms ease",
                    }}
                  >
                    <div style={{
                      position: "absolute",
                      top: 2,
                      left: d.enabled ? 14 : 2,
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      background: "white",
                      transition: "left 120ms ease",
                      boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
                    }} />
                  </div>

                  {/* Time range */}
                  {d.enabled ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 8, paddingLeft: 8 }}>
                      <input
                        type="time"
                        value={d.start}
                        onChange={(e) => patchDay(day, { start: e.target.value })}
                        style={{
                          height: 32,
                          padding: "0 8px",
                          border: "1px solid var(--color-border-primary)",
                          borderRadius: 6,
                          background: "var(--color-bg-primary)",
                          fontFamily: "var(--font-family-base)",
                          fontSize: 12,
                          fontWeight: 500,
                          color: "var(--color-content-primary)",
                          outline: "none",
                          cursor: "pointer",
                        }}
                      />
                      <span style={{ fontSize: 12, color: "var(--color-content-tertiary)", fontFamily: "var(--font-family-base)" }}>→</span>
                      <input
                        type="time"
                        value={d.end}
                        onChange={(e) => patchDay(day, { end: e.target.value })}
                        style={{
                          height: 32,
                          padding: "0 8px",
                          border: "1px solid var(--color-border-primary)",
                          borderRadius: 6,
                          background: "var(--color-bg-primary)",
                          fontFamily: "var(--font-family-base)",
                          fontSize: 12,
                          fontWeight: 500,
                          color: "var(--color-content-primary)",
                          outline: "none",
                          cursor: "pointer",
                        }}
                      />
                    </div>
                  ) : (
                    <span style={{ paddingLeft: 8, fontFamily: "var(--font-family-base)", fontSize: 12, fontWeight: 500, color: "var(--color-content-tertiary)" }}>
                      Closed
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── General Settings Content ─────────────────────────────────────────────────

function GeneralContent() {
  const [companyName, setCompanyName] = React.useState("Acme Technologies");
  const [description, setDescription] = React.useState("Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.");
  const [website, setWebsite] = React.useState("www.makula.io");
  const [location, setLocation] = React.useState("Berlin, Germany");
  const [currency, setCurrency] = React.useState("eur");
  const [logoPreview, setLogoPreview] = React.useState<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const currencies = [
    { value: "eur", label: "EUR (Euro)" },
    { value: "usd", label: "USD (Dollar)" },
    { value: "gbp", label: "GBP (Pound)" },
    { value: "chf", label: "CHF (Franc)" },
    { value: "sek", label: "SEK (Krona)" },
  ];

  function handleLogoUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setLogoPreview(ev.target?.result as string);
    reader.readAsDataURL(file);
  }

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: "24px 32px 48px" }}>
      <div style={{ maxWidth: 880, display: "flex", flexDirection: "column", gap: 32 }}>

        {/* Section header */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16 }}>
          <div>
            <h2 style={{ margin: "0 0 6px", fontSize: 16, fontWeight: 700, color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)", letterSpacing: "-0.32px", lineHeight: "24px" }}>
              Company Information
            </h2>
            <p style={{ margin: 0, fontSize: 14, fontWeight: 500, color: "var(--color-content-secondary)", fontFamily: "var(--font-family-base)", lineHeight: "20px" }}>
              Configure your company name, description, location, and website for accurate profile settings.
            </p>
          </div>
          <button
            style={{
              height: 32,
              padding: "0 14px",
              border: "none",
              borderRadius: 8,
              background: "var(--color-bg-brand-dark)",
              fontFamily: "var(--font-family-base)",
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: "-0.24px",
              color: "var(--color-content-inverse)",
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            Save
          </button>
        </div>

        {/* Divider */}
        <div style={{ height: 1, background: "var(--color-border-primary)" }} />

        {/* Form */}
        <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>

          {/* Logo */}
          <FormField label="Logo">
            <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
              {/* Logo preview box */}
              <div
                onClick={() => fileInputRef.current?.click()}
                style={{
                  width: 120,
                  height: 120,
                  border: "1px solid var(--color-border-primary)",
                  borderRadius: 8,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "var(--color-bg-primary)",
                  flexShrink: 0,
                  cursor: "pointer",
                  overflow: "hidden",
                }}
              >
                {logoPreview ? (
                  <img src={logoPreview} alt="Company logo" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                ) : (
                  <IcImage />
                )}
              </div>

              {/* Upload actions */}
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    alignSelf: "flex-start",
                    height: 32,
                    padding: "0 14px",
                    border: "none",
                    borderRadius: 8,
                    background: "var(--color-bg-brand-dark)",
                    fontFamily: "var(--font-family-base)",
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: "-0.24px",
                    color: "var(--color-content-inverse)",
                    cursor: "pointer",
                  }}
                >
                  Upload
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg"
                  style={{ display: "none" }}
                  onChange={handleLogoUpload}
                />
                <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {["Supported image format: PNG, JPEG", "Supported image size: 500KB", "Recommended dimensions: 400 × 400"].map((t) => (
                    <span key={t} style={{ fontFamily: "var(--font-family-base)", fontSize: 12, fontWeight: 500, color: "var(--color-content-secondary)", lineHeight: "16px" }}>{t}</span>
                  ))}
                </div>
              </div>
            </div>
          </FormField>

          {/* Company Name */}
          <FormField label="Company Name" style={{ maxWidth: 415 }}>
            <TextInput value={companyName} onChange={setCompanyName} placeholder="Enter company name" />
          </FormField>

          {/* Company Description */}
          <FormField label="Company Description" hint="Add a short description of your company shown in the customer portal.">
            <DescriptionEditor value={description} onChange={setDescription} />
          </FormField>

          {/* Website + Location */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, maxWidth: 846 }}>
            <FormField label="Website">
              <TextInput value={website} onChange={setWebsite} placeholder="e.g. www.company.com" />
            </FormField>
            <FormField label="Location">
              <TextInput value={location} onChange={setLocation} placeholder="e.g. Berlin, Germany" />
            </FormField>
          </div>

          {/* Currency */}
          <FormField label="Currency" style={{ maxWidth: 415 }}>
            <SelectInput value={currency} onChange={setCurrency} options={currencies} />
          </FormField>

        </div>

        {/* Divider */}
        <div style={{ height: 1, background: "var(--color-border-primary)" }} />

        {/* Business Hours */}
        <BusinessHoursSection />

      </div>
    </div>
  );
}

// ─── SLA Configuration ────────────────────────────────────────────────────────

type SLAFeature = "work-orders" | "tickets";
type SLAConditionType = "start" | "pause" | "cancel";
type SLAUnit = "minutes" | "hours" | "days";

interface SLACondition {
  id: string;
  type: SLAConditionType;
  feature: SLAFeature;
  event: string;
}

interface SLAGoalCondition {
  id: string;
  feature: SLAFeature;
  event: string;
}

interface SLADefinition {
  id: string;
  name: string;
  description: string;
  goalValue: number;
  goalUnit: SLAUnit;
  goalConditions: SLAGoalCondition[];
  features: SLAFeature[];
  conditions: SLACondition[];
}

const FEATURE_LABELS: Record<SLAFeature, string> = {
  "work-orders": "Work Orders",
  "tickets": "Tickets",
};

const CONDITION_TYPE_META: Record<SLAConditionType, { label: string; color: string; bg: string; description: string }> = {
  start:  { label: "Start",  color: "#1D4ED8", bg: "#EFF6FF", description: "Timer begins when this event occurs" },
  pause:  { label: "Pause",  color: "#92400E", bg: "#FEF9C3", description: "Timer pauses while this condition is active" },
  cancel: { label: "Cancel", color: "#991B1B", bg: "#FEF2F2", description: "Timer is cancelled when this event occurs" },
};

const TRIGGER_EVENTS: Record<SLAFeature, string[]> = {
  "work-orders": [
    "Work order created",
    "Status changed to Open",
    "Status changed to In Progress",
    "Status changed to On Hold",
    "Status changed to Onsite",
    "Status changed to Completed",
    "Status changed to Cancelled",
    "Technician assigned",
    "Technician arrived on site",
    "Customer confirmed resolution",
    "Priority changed to Critical",
    "Priority changed to High",
  ],
  "tickets": [
    "Ticket created",
    "Status changed to Open",
    "Status changed to In Progress",
    "Status changed to Pending Customer",
    "Status changed to Resolved",
    "Status changed to Closed",
    "First reply sent",
    "Customer replied",
    "Agent assigned",
    "Priority changed to Critical",
    "Priority changed to High",
    "Escalated",
  ],
};

const INITIAL_SLA_DEFINITIONS: SLADefinition[] = [
  {
    id: "sla-onsite",
    name: "Time to Onsite",
    description: "Time from work order creation until technician arrives on site",
    goalValue: 2, goalUnit: "hours",
    goalConditions: [
      { id: "g1", feature: "work-orders", event: "Technician arrived on site" },
    ],
    features: ["work-orders"],
    conditions: [
      { id: "c1", type: "start",  feature: "work-orders", event: "Work order created" },
      { id: "c2", type: "pause",  feature: "work-orders", event: "Status changed to On Hold" },
      { id: "c3", type: "cancel", feature: "work-orders", event: "Status changed to Cancelled" },
    ],
  },
  {
    id: "sla-response",
    name: "Time to First Response",
    description: "Time from ticket creation until the first agent reply is sent",
    goalValue: 4, goalUnit: "hours",
    goalConditions: [
      { id: "g2", feature: "tickets", event: "First reply sent" },
    ],
    features: ["tickets"],
    conditions: [
      { id: "c4", type: "start",  feature: "tickets", event: "Ticket created" },
      { id: "c5", type: "pause",  feature: "tickets", event: "Status changed to Pending Customer" },
      { id: "c6", type: "cancel", feature: "tickets", event: "Status changed to Closed" },
    ],
  },
  {
    id: "sla-resolution",
    name: "Time to Resolution",
    description: "Time from creation until the issue is fully resolved and closed",
    goalValue: 24, goalUnit: "hours",
    goalConditions: [
      { id: "g3", feature: "work-orders", event: "Status changed to Completed" },
      { id: "g4", feature: "tickets",     event: "Status changed to Resolved" },
    ],
    features: ["work-orders", "tickets"],
    conditions: [
      { id: "c7",  type: "start",  feature: "work-orders", event: "Work order created" },
      { id: "c8",  type: "pause",  feature: "work-orders", event: "Status changed to On Hold" },
      { id: "c9",  type: "cancel", feature: "work-orders", event: "Status changed to Cancelled" },
      { id: "c10", type: "start",  feature: "tickets",     event: "Ticket created" },
      { id: "c11", type: "pause",  feature: "tickets",     event: "Status changed to Pending Customer" },
      { id: "c12", type: "cancel", feature: "tickets",     event: "Status changed to Closed" },
    ],
  },
];

function SLAConfigContent() {
  const [definitions, setDefinitions] = React.useState<SLADefinition[]>(INITIAL_SLA_DEFINITIONS);
  const [openId, setOpenId] = React.useState<string | null>("sla-onsite");

  function addDefinition() {
    const id = `sla-${Date.now()}`;
    const newDef: SLADefinition = {
      id, name: "New SLA", description: "", goalValue: 8, goalUnit: "hours", goalConditions: [],
      features: ["work-orders"], conditions: [],
    };
    setDefinitions(prev => [...prev, newDef]);
    setOpenId(id);
  }

  function updateDef(id: string, patch: Partial<SLADefinition>) {
    setDefinitions(prev => prev.map(d => d.id === id ? { ...d, ...patch } : d));
  }

  function toggleFeature(id: string, feature: SLAFeature) {
    const def = definitions.find(d => d.id === id)!;
    const has = def.features.includes(feature);
    updateDef(id, { features: has ? def.features.filter(f => f !== feature) : [...def.features, feature] });
  }

  function addCondition(id: string, type: SLAConditionType) {
    const def = definitions.find(d => d.id === id)!;
    const defaultFeature = def.features[0] ?? "work-orders";
    const newCond: SLACondition = {
      id: `c${Date.now()}`, type, feature: defaultFeature,
      event: TRIGGER_EVENTS[defaultFeature][0],
    };
    updateDef(id, { conditions: [...def.conditions, newCond] });
  }

  function updateCondition(defId: string, condId: string, patch: Partial<SLACondition>) {
    const def = definitions.find(d => d.id === defId)!;
    updateDef(defId, {
      conditions: def.conditions.map(c => {
        if (c.id !== condId) return c;
        const updated = { ...c, ...patch };
        // reset event when feature changes
        if (patch.feature && patch.feature !== c.feature) {
          updated.event = TRIGGER_EVENTS[patch.feature][0];
        }
        return updated;
      }),
    });
  }

  function removeCondition(defId: string, condId: string) {
    const def = definitions.find(d => d.id === defId)!;
    updateDef(defId, { conditions: def.conditions.filter(c => c.id !== condId) });
  }

  function addGoalCondition(defId: string) {
    const def = definitions.find(d => d.id === defId)!;
    const defaultFeature = def.features[0] ?? "work-orders";
    const newCond: SLAGoalCondition = {
      id: `g${Date.now()}`, feature: defaultFeature,
      event: TRIGGER_EVENTS[defaultFeature][0],
    };
    updateDef(defId, { goalConditions: [...def.goalConditions, newCond] });
  }

  function updateGoalCondition(defId: string, condId: string, patch: Partial<SLAGoalCondition>) {
    const def = definitions.find(d => d.id === defId)!;
    updateDef(defId, {
      goalConditions: def.goalConditions.map(c => {
        if (c.id !== condId) return c;
        const updated = { ...c, ...patch };
        if (patch.feature && patch.feature !== c.feature) {
          updated.event = TRIGGER_EVENTS[patch.feature][0];
        }
        return updated;
      }),
    });
  }

  function removeGoalCondition(defId: string, condId: string) {
    const def = definitions.find(d => d.id === defId)!;
    updateDef(defId, { goalConditions: def.goalConditions.filter(c => c.id !== condId) });
  }

  const selectStyle: React.CSSProperties = {
    height: 32, padding: "0 8px", border: "1px solid var(--color-border-primary)",
    borderRadius: 7, background: "var(--color-bg-primary)", fontFamily: "var(--font-family-base)",
    fontSize: 12, color: "var(--color-content-primary)", outline: "none", cursor: "pointer",
  };

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: "24px 32px 48px" }}>
      <div style={{ maxWidth: 800, display: "flex", flexDirection: "column", gap: 24 }}>

        {/* Header */}
        <div>
          <h2 style={{ margin: "0 0 6px", fontSize: 16, fontWeight: 700, color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)", letterSpacing: "-0.32px" }}>
            SLA Configuration
          </h2>
          <p style={{ margin: 0, fontSize: 14, fontWeight: 500, color: "var(--color-content-secondary)", fontFamily: "var(--font-family-base)", lineHeight: "20px" }}>
            Define SLA agreements, their time targets, which features they apply to, and the conditions that start, pause, or cancel the timer.
          </p>
        </div>

        <div style={{ height: 1, background: "var(--color-border-primary)" }} />

        {/* Accordion cards */}
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {definitions.map(def => {
            const isOpen = openId === def.id;
            const conditionsByType = (type: SLAConditionType) => def.conditions.filter(c => c.type === type);

            return (
              <div
                key={def.id}
                style={{ border: `1.5px solid ${isOpen ? "var(--color-content-brand)" : "var(--color-border-primary)"}`, borderRadius: 12, overflow: "hidden", transition: "border-color 120ms ease" }}
              >
                {/* Accordion header */}
                <button
                  onClick={() => setOpenId(isOpen ? null : def.id)}
                  style={{ width: "100%", display: "flex", alignItems: "center", gap: 14, padding: "16px 20px", background: isOpen ? "color-mix(in srgb, var(--color-content-brand) 4%, var(--color-bg-primary))" : "var(--color-bg-primary)", border: "none", cursor: "pointer", textAlign: "left", transition: "background 120ms ease" }}
                >
                  {/* Clock icon */}
                  <div style={{ width: 36, height: 36, borderRadius: 9, background: isOpen ? "color-mix(in srgb, var(--color-content-brand) 10%, var(--color-bg-primary))" : "var(--color-bg-accent)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, color: isOpen ? "var(--color-content-brand)" : "var(--color-content-secondary)", transition: "all 120ms ease" }}>
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="9" cy="9" r="7" />
                      <path d="M9 5v4l2.5 2" />
                    </svg>
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
                      <span style={{ fontSize: 14, fontWeight: 700, color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)", letterSpacing: "-0.14px" }}>
                        {def.name}
                      </span>
                      {/* Feature badges */}
                      {def.features.map(f => (
                        <span key={f} style={{ fontSize: 11, fontWeight: 700, borderRadius: 4, padding: "1px 6px", background: "#EFF6FF", color: "#1D4ED8", fontFamily: "var(--font-family-base)" }}>
                          {FEATURE_LABELS[f]}
                        </span>
                      ))}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <span style={{ fontSize: 12, fontWeight: 500, color: "var(--color-content-secondary)", fontFamily: "var(--font-family-base)" }}>
                        {def.description}
                      </span>
                    </div>
                  </div>

                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="var(--color-content-secondary)" strokeWidth="1.5" strokeLinecap="round" style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 200ms ease", flexShrink: 0 }}>
                    <path d="M4 6l4 4 4-4" />
                  </svg>
                </button>

                {/* Accordion body */}
                {isOpen && (
                  <div style={{ borderTop: "1px solid var(--color-border-primary)" }}>

                    {/* ── Name & Description ── */}
                    <div style={{ padding: "20px 20px 16px", borderBottom: "1px solid var(--color-border-primary)", display: "flex", flexDirection: "column", gap: 12 }}>
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: "var(--color-content-tertiary)", fontFamily: "var(--font-family-base)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>Name</div>
                        <input
                          type="text"
                          value={def.name}
                          onChange={e => updateDef(def.id, { name: e.target.value })}
                          placeholder="SLA name…"
                          style={{ width: "100%", height: 34, padding: "0 12px", border: "1px solid var(--color-border-primary)", borderRadius: 7, background: "var(--color-bg-primary)", fontFamily: "var(--font-family-base)", fontSize: 13, fontWeight: 600, color: "var(--color-content-primary)", outline: "none", boxSizing: "border-box" }}
                        />
                      </div>
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: "var(--color-content-tertiary)", fontFamily: "var(--font-family-base)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>Description</div>
                        <input
                          type="text"
                          value={def.description}
                          onChange={e => updateDef(def.id, { description: e.target.value })}
                          placeholder="Short description…"
                          style={{ width: "100%", height: 34, padding: "0 12px", border: "1px solid var(--color-border-primary)", borderRadius: 7, background: "var(--color-bg-primary)", fontFamily: "var(--font-family-base)", fontSize: 13, fontWeight: 500, color: "var(--color-content-primary)", outline: "none", boxSizing: "border-box" }}
                        />
                      </div>
                    </div>

                    {/* ── Goal ── */}
                    <div style={{ padding: "20px 20px 16px", borderBottom: "1px solid var(--color-border-primary)" }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "var(--color-content-tertiary)", fontFamily: "var(--font-family-base)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 16 }}>
                        Goal
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                        {/* Goal conditions — "SLA is met when…" */}
                        <div>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <span style={{ fontSize: 12, fontWeight: 700, borderRadius: 5, padding: "2px 8px", background: "#F0FDF4", color: "#15803D", fontFamily: "var(--font-family-base)" }}>
                                Met
                              </span>
                              <span style={{ fontSize: 12, fontWeight: 500, color: "var(--color-content-secondary)", fontFamily: "var(--font-family-base)" }}>
                                SLA is considered met when this event occurs
                              </span>
                            </div>
                            <button
                              onClick={() => addGoalCondition(def.id)}
                              style={{ display: "flex", alignItems: "center", gap: 4, height: 28, padding: "0 10px", border: "1px dashed var(--color-border-primary)", borderRadius: 6, background: "transparent", color: "var(--color-content-secondary)", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "var(--font-family-base)" }}
                            >
                              <IcPlus /> Add
                            </button>
                          </div>

                          {def.goalConditions.length === 0 ? (
                            <div style={{ padding: "10px 14px", border: "1px dashed var(--color-border-primary)", borderRadius: 8, fontSize: 12, color: "var(--color-content-tertiary)", fontFamily: "var(--font-family-base)", fontStyle: "italic" }}>
                              No conditions — SLA will never be marked as met.
                            </div>
                          ) : (
                            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                              {def.goalConditions.map((cond, i) => (
                                <div key={cond.id} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                  <span style={{ fontSize: 12, fontWeight: 500, color: "var(--color-content-tertiary)", fontFamily: "var(--font-family-base)", width: 32, flexShrink: 0 }}>
                                    {i === 0 ? "When" : "or"}
                                  </span>
                                  <select
                                    value={cond.feature}
                                    onChange={e => updateGoalCondition(def.id, cond.id, { feature: e.target.value as SLAFeature })}
                                    style={selectStyle}
                                  >
                                    {def.features.map(f => (
                                      <option key={f} value={f}>{FEATURE_LABELS[f]}</option>
                                    ))}
                                    {def.features.length === 0 && (
                                      <option value={cond.feature}>{FEATURE_LABELS[cond.feature]}</option>
                                    )}
                                  </select>
                                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="var(--color-content-tertiary)" strokeWidth="1.4" strokeLinecap="round"><path d="M3 7h8M8 4l3 3-3 3" /></svg>
                                  <select
                                    value={cond.event}
                                    onChange={e => updateGoalCondition(def.id, cond.id, { event: e.target.value })}
                                    style={{ ...selectStyle, flex: 1 }}
                                  >
                                    {TRIGGER_EVENTS[cond.feature].map(ev => (
                                      <option key={ev} value={ev}>{ev}</option>
                                    ))}
                                  </select>
                                  <button
                                    onClick={() => removeGoalCondition(def.id, cond.id)}
                                    style={{ width: 28, height: 28, border: "1px solid var(--color-border-primary)", borderRadius: 6, background: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--color-content-tertiary)", flexShrink: 0 }}
                                  >
                                    <svg width="11" height="11" viewBox="0 0 11 11" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M1 1l9 9M10 1L1 10" /></svg>
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* ── Applies To ── */}
                    <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--color-border-primary)" }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "var(--color-content-tertiary)", fontFamily: "var(--font-family-base)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 12 }}>
                        Applies To
                      </div>
                      <div style={{ display: "flex", gap: 10 }}>
                        {(["work-orders", "tickets"] as SLAFeature[]).map(f => {
                          const active = def.features.includes(f);
                          return (
                            <button
                              key={f}
                              onClick={() => toggleFeature(def.id, f)}
                              style={{ display: "flex", alignItems: "center", gap: 7, height: 34, padding: "0 14px", border: `1.5px solid ${active ? "var(--color-content-brand)" : "var(--color-border-primary)"}`, borderRadius: 8, background: active ? "color-mix(in srgb, var(--color-content-brand) 8%, var(--color-bg-primary))" : "var(--color-bg-primary)", color: active ? "var(--color-content-brand)" : "var(--color-content-secondary)", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "var(--font-family-base)", transition: "all 100ms ease" }}
                            >
                              {active && (
                                <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M2 6.5l3 3 6-6" /></svg>
                              )}
                              {FEATURE_LABELS[f]}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* ── Conditions ── */}
                    <div style={{ padding: "16px 20px" }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "var(--color-content-tertiary)", fontFamily: "var(--font-family-base)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 16 }}>
                        Conditions
                      </div>

                      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                        {(["start", "pause", "cancel"] as SLAConditionType[]).map(condType => {
                          const meta = CONDITION_TYPE_META[condType];
                          const rows = conditionsByType(condType);
                          return (
                            <div key={condType}>
                              {/* Condition type header */}
                              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                  <span style={{ fontSize: 12, fontWeight: 700, borderRadius: 5, padding: "2px 8px", background: meta.bg, color: meta.color, fontFamily: "var(--font-family-base)" }}>
                                    {meta.label}
                                  </span>
                                  <span style={{ fontSize: 12, fontWeight: 500, color: "var(--color-content-secondary)", fontFamily: "var(--font-family-base)" }}>
                                    {meta.description}
                                  </span>
                                </div>
                                <button
                                  onClick={() => addCondition(def.id, condType)}
                                  style={{ display: "flex", alignItems: "center", gap: 4, height: 28, padding: "0 10px", border: "1px dashed var(--color-border-primary)", borderRadius: 6, background: "transparent", color: "var(--color-content-secondary)", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "var(--font-family-base)" }}
                                >
                                  <IcPlus /> Add
                                </button>
                              </div>

                              {/* Condition rows */}
                              {rows.length === 0 ? (
                                <div style={{ padding: "10px 14px", border: "1px dashed var(--color-border-primary)", borderRadius: 8, fontSize: 12, color: "var(--color-content-tertiary)", fontFamily: "var(--font-family-base)", fontStyle: "italic" }}>
                                  No {condType} conditions — timer will {condType === "start" ? "start immediately" : condType === "pause" ? "never pause" : "never be cancelled"}.
                                </div>
                              ) : (
                                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                  {rows.map((cond, i) => (
                                    <div key={cond.id} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                      {/* "When" label */}
                                      <span style={{ fontSize: 12, fontWeight: 500, color: "var(--color-content-tertiary)", fontFamily: "var(--font-family-base)", width: 32, flexShrink: 0 }}>
                                        {i === 0 ? "When" : "or"}
                                      </span>
                                      {/* Feature selector */}
                                      <select
                                        value={cond.feature}
                                        onChange={e => updateCondition(def.id, cond.id, { feature: e.target.value as SLAFeature })}
                                        style={selectStyle}
                                      >
                                        {def.features.map(f => (
                                          <option key={f} value={f}>{FEATURE_LABELS[f]}</option>
                                        ))}
                                        {def.features.length === 0 && (
                                          <option value={cond.feature}>{FEATURE_LABELS[cond.feature]}</option>
                                        )}
                                      </select>
                                      {/* Arrow */}
                                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="var(--color-content-tertiary)" strokeWidth="1.4" strokeLinecap="round"><path d="M3 7h8M8 4l3 3-3 3" /></svg>
                                      {/* Event selector */}
                                      <select
                                        value={cond.event}
                                        onChange={e => updateCondition(def.id, cond.id, { event: e.target.value })}
                                        style={{ ...selectStyle, flex: 1 }}
                                      >
                                        {TRIGGER_EVENTS[cond.feature].map(ev => (
                                          <option key={ev} value={ev}>{ev}</option>
                                        ))}
                                      </select>
                                      {/* Remove */}
                                      <button
                                        onClick={() => removeCondition(def.id, cond.id)}
                                        style={{ width: 28, height: 28, border: "1px solid var(--color-border-primary)", borderRadius: 6, background: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--color-content-tertiary)", flexShrink: 0 }}
                                      >
                                        <svg width="11" height="11" viewBox="0 0 11 11" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M1 1l9 9M10 1L1 10" /></svg>
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Footer save */}
                    <div style={{ padding: "12px 20px", borderTop: "1px solid var(--color-border-primary)", display: "flex", justifyContent: "flex-end" }}>
                      <SaveButton onClick={() => {}} />
                    </div>

                  </div>
                )}
              </div>
            );
          })}

          {/* Add new SLA */}
          <button
            onClick={addDefinition}
            style={{ display: "flex", alignItems: "center", gap: 6, height: 36, padding: "0 4px", background: "none", border: "none", cursor: "pointer", color: "var(--color-content-brand)", fontSize: 13, fontWeight: 600, fontFamily: "var(--font-family-base)", alignSelf: "flex-start" }}
          >
            <IcPlus /> Add new SLA
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Service Packages ─────────────────────────────────────────────────────────

interface PackageLineItem {
  id: string;
  name: string;
  qty: number;
  unit: string;
  price: number;
}

interface ServicePackage {
  id: string;
  name: string;
  description: string;
  packageTypeId: string;
  price: number;
  unit: string;
  lineItems: PackageLineItem[];
}

const INITIAL_PACKAGES: ServicePackage[] = [
  {
    id: "sp1", name: "Basic Support Package", description: "Email & phone support, 5×8, 4h response SLA",
    packageTypeId: "pt2", price: 299, unit: "month",
    lineItems: [
      { id: "li1", name: "Remote Support Session", qty: 5, unit: "trip", price: 0 },
      { id: "li2", name: "Monthly Health Report",  qty: 1, unit: "each", price: 0 },
    ],
  },
  {
    id: "sp2", name: "Annual Maintenance Contract", description: "12× scheduled PM visits + parts included",
    packageTypeId: "pt1", price: 3600, unit: "year",
    lineItems: [
      { id: "li3", name: "Preventive Maintenance Visit", qty: 12, unit: "each", price: 280 },
      { id: "li4", name: "Oil Filter — OEM Grade",       qty: 12, unit: "each", price: 28  },
      { id: "li5", name: "Travel Allowance",             qty: 12, unit: "trip", price: 0   },
    ],
  },
  {
    id: "sp3", name: "Asset Installation Package", description: "Full install & commissioning including site visit",
    packageTypeId: "pt3", price: 1200, unit: "each",
    lineItems: [
      { id: "li6", name: "Asset Installation",           qty: 1, unit: "each", price: 800 },
      { id: "li7", name: "Installation & Commissioning", qty: 4, unit: "hour", price: 0   },
    ],
  },
  {
    id: "sp4", name: "Full-Coverage Service Plan", description: "Unlimited callouts, parts & labor all-inclusive",
    packageTypeId: "pt2", price: 5500, unit: "year",
    lineItems: [
      { id: "li8",  name: "Preventive Maintenance",  qty: 1, unit: "each", price: 0   },
      { id: "li9",  name: "Ball Bearing 6205-ZZ",    qty: 4, unit: "each", price: 18  },
      { id: "li10", name: "V-Belt A42 Industrial",   qty: 2, unit: "each", price: 34  },
    ],
  },
];

function FinanceServicePackagesContent() {
  const { config: financeConfig } = useFinanceConfig();
  const packageTypes = financeConfig.packageTypes;
  const [packages, setPackages] = React.useState<ServicePackage[]>(INITIAL_PACKAGES);
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [editingPkg, setEditingPkg] = React.useState<ServicePackage | null>(null);
  const [typeFilter, setTypeFilter] = React.useState<string>("all");

  function openNew() {
    setEditingPkg({
      id: `sp${Date.now()}`, name: "", description: "",
      packageTypeId: packageTypes[0]?.id ?? "", price: 0, unit: "month", lineItems: [],
    });
    setDrawerOpen(true);
  }

  function openEdit(pkg: ServicePackage) {
    setEditingPkg({ ...pkg, lineItems: pkg.lineItems.map(li => ({ ...li })) });
    setDrawerOpen(true);
  }

  function handleSave(pkg: ServicePackage) {
    setPackages(prev => {
      const idx = prev.findIndex(p => p.id === pkg.id);
      if (idx >= 0) { const next = [...prev]; next[idx] = pkg; return next; }
      return [...prev, pkg];
    });
    setDrawerOpen(false);
  }

  function handleDelete(id: string) {
    setPackages(prev => prev.filter(p => p.id !== id));
  }

  const filtered = typeFilter === "all" ? packages : packages.filter(p => p.packageTypeId === typeFilter);

  const divider = <div style={{ height: 1, background: "var(--color-border-primary)" }} />;

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: "24px 32px 48px", position: "relative" }}>
      <div style={{ maxWidth: 880, display: "flex", flexDirection: "column", gap: 32 }}>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16 }}>
          <div>
            <h2 style={{ margin: "0 0 6px", fontSize: 16, fontWeight: 700, color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)", letterSpacing: "-0.32px", lineHeight: "24px" }}>
              Service Packages
            </h2>
            <p style={{ margin: 0, fontSize: 14, fontWeight: 500, color: "var(--color-content-secondary)", fontFamily: "var(--font-family-base)", lineHeight: "20px" }}>
              Define reusable packages with pricing and line items. Packages appear in Browse Items when creating quotations and invoices.
            </p>
          </div>
          <button
            onClick={openNew}
            style={{ display: "flex", alignItems: "center", gap: 6, height: 36, padding: "0 14px", border: "none", borderRadius: 8, background: "var(--color-content-brand)", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "var(--font-family-base)", flexShrink: 0 }}
          >
            <IcPlus /> New Package
          </button>
        </div>

        {divider}

        {/* Package type filter tabs */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {(["all", ...packageTypes.map(pt => pt.id)]).map(key => {
            const label = key === "all" ? "All packages" : (packageTypes.find(pt => pt.id === key)?.name ?? key);
            const active = typeFilter === key;
            return (
              <button
                key={key}
                onClick={() => setTypeFilter(key)}
                style={{
                  height: 32, padding: "0 14px", border: "1px solid", borderRadius: 20,
                  borderColor: active ? "var(--color-content-brand)" : "var(--color-border-primary)",
                  background: active ? "var(--color-bg-brand-lightest)" : "var(--color-bg-primary)",
                  color: active ? "var(--color-content-brand)" : "var(--color-content-secondary)",
                  fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "var(--font-family-base)",
                  transition: "all 100ms ease",
                }}
              >
                {label}
              </button>
            );
          })}
          <span style={{ marginLeft: "auto", fontSize: 12, fontWeight: 500, color: "var(--color-content-secondary)", fontFamily: "var(--font-family-base)", alignSelf: "center" }}>
            {filtered.length} package{filtered.length !== 1 ? "s" : ""}
          </span>
        </div>

        {/* Package list */}
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {filtered.length === 0 && (
            <div style={{ textAlign: "center", padding: "48px 0", color: "var(--color-content-secondary)", fontSize: 14, fontFamily: "var(--font-family-base)" }}>
              No packages yet — click <strong>New Package</strong> to create one.
            </div>
          )}
          {filtered.map(pkg => {
            const pkgTypeName = packageTypes.find(pt => pt.id === pkg.packageTypeId)?.name ?? pkg.packageTypeId;
            return (
              <div
                key={pkg.id}
                style={{ border: "1px solid var(--color-border-primary)", borderRadius: 10, background: "var(--color-bg-primary)", overflow: "hidden" }}
              >
                {/* Package header row */}
                <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px" }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
                      <span style={{ fontSize: 14, fontWeight: 700, color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)", letterSpacing: "-0.14px" }}>
                        {pkg.name}
                      </span>
                      <span style={{ fontSize: 11, fontWeight: 700, borderRadius: 4, padding: "1px 6px", background: "#EEF0FF", color: "#0517F8", fontFamily: "var(--font-family-base)", flexShrink: 0 }}>
                        {pkgTypeName}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 500, color: "var(--color-content-secondary)", fontFamily: "var(--font-family-base)" }}>
                      {pkg.description}
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontSize: 15, fontWeight: 700, color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)", letterSpacing: "-0.2px" }}>
                        € {pkg.price.toLocaleString()}
                      </div>
                      <div style={{ fontSize: 11, fontWeight: 500, color: "var(--color-content-secondary)", fontFamily: "var(--font-family-base)" }}>
                        per {pkg.unit}
                      </div>
                    </div>
                    <button
                      onClick={() => openEdit(pkg)}
                      style={{ height: 32, padding: "0 12px", border: "1px solid var(--color-border-primary)", borderRadius: 7, background: "var(--color-bg-primary)", color: "var(--color-content-secondary)", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "var(--font-family-base)" }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(pkg.id)}
                      style={{ height: 32, width: 32, border: "1px solid var(--color-border-primary)", borderRadius: 7, background: "var(--color-bg-primary)", color: "var(--color-content-secondary)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                    >
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"><path d="M2 4h10M5 4V3a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v1M5.5 6.5v4M8.5 6.5v4M3.5 4l.5 7.5a1 1 0 0 0 1 .5h4a1 1 0 0 0 1-.5L11 4" /></svg>
                    </button>
                  </div>
                </div>

                {/* Line items preview */}
                {pkg.lineItems.length > 0 && (
                  <div style={{ borderTop: "1px solid var(--color-border-primary)", background: "var(--color-bg-accent)" }}>
                    {pkg.lineItems.map((li, i) => (
                      <div
                        key={li.id}
                        style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 16px", borderTop: i > 0 ? "1px solid var(--color-border-primary)" : "none" }}
                      >
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="var(--color-content-tertiary)" strokeWidth="1.3" strokeLinecap="round"><path d="M2 6h8M6 2l4 4-4 4" /></svg>
                        <span style={{ flex: 1, fontSize: 12, fontWeight: 500, color: "var(--color-content-secondary)", fontFamily: "var(--font-family-base)" }}>
                          {li.name}
                        </span>
                        <span style={{ fontSize: 12, fontWeight: 500, color: "var(--color-content-tertiary)", fontFamily: "var(--font-family-base)" }}>
                          ×{li.qty} {li.unit}
                        </span>
                        <span style={{ fontSize: 12, fontWeight: 600, color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)", minWidth: 56, textAlign: "right" }}>
                          {li.price > 0 ? `€ ${(li.qty * li.price).toLocaleString()}` : "—"}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Edit / Create drawer */}
      {drawerOpen && editingPkg && (
        <ServicePackageDrawer
          pkg={editingPkg}
          onSave={handleSave}
          onClose={() => setDrawerOpen(false)}
        />
      )}
    </div>
  );
}

function ServicePackageDrawer({
  pkg: initial,
  onSave,
  onClose,
}: {
  pkg: ServicePackage;
  onSave: (pkg: ServicePackage) => void;
  onClose: () => void;
}) {
  const { config: financeConfig } = useFinanceConfig();
  const packageTypes = financeConfig.packageTypes;
  const [name, setName] = React.useState(initial.name);
  const [description, setDescription] = React.useState(initial.description);
  const [packageTypeId, setPackageTypeId] = React.useState(initial.packageTypeId || packageTypes[0]?.id || "");
  const [price, setPrice] = React.useState(String(initial.price));
  const [unit, setUnit] = React.useState(initial.unit);
  const [lineItems, setLineItems] = React.useState<PackageLineItem[]>(initial.lineItems);

  const isNew = initial.name === "";

  function addLineItem() {
    setLineItems(prev => [...prev, { id: `li${Date.now()}`, name: "", qty: 1, unit: "each", price: 0 }]);
  }

  function updateLineItem(id: string, field: keyof PackageLineItem, value: string | number) {
    setLineItems(prev => prev.map(li => li.id === id ? { ...li, [field]: value } : li));
  }

  function removeLineItem(id: string) {
    setLineItems(prev => prev.filter(li => li.id !== id));
  }

  function handleSave() {
    if (!name.trim()) return;
    onSave({ ...initial, name: name.trim(), description, packageTypeId, price: parseFloat(price) || 0, unit, lineItems });
  }

  const inputStyle: React.CSSProperties = {
    width: "100%", padding: "8px 10px", border: "1px solid var(--color-border-primary)",
    borderRadius: 7, background: "var(--color-bg-primary)", fontFamily: "var(--font-family-base)",
    fontSize: 13, color: "var(--color-content-primary)", outline: "none", boxSizing: "border-box",
  };

  const labelStyle: React.CSSProperties = {
    display: "block", fontSize: 12, fontWeight: 600, color: "var(--color-content-primary)",
    fontFamily: "var(--font-family-base)", marginBottom: 6,
  };

  const UNIT_OPTIONS = ["month", "quarter", "year", "each", "visit", "block", "session", "trip", "day", "hour"];

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(23,43,77,0.3)" }}
      />
      {/* Drawer */}
      <div style={{
        position: "fixed", top: 0, right: 0, bottom: 0, zIndex: 201,
        width: 480, background: "var(--color-bg-primary)",
        boxShadow: "-4px 0 32px rgba(23,43,77,0.14)",
        display: "flex", flexDirection: "column", overflow: "hidden",
      }}>
        {/* Drawer header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "18px 24px", borderBottom: "1px solid var(--color-border-primary)", flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)", letterSpacing: "-0.2px" }}>
              {isNew ? "New Service Package" : "Edit Service Package"}
            </div>
            <div style={{ fontSize: 12, fontWeight: 500, color: "var(--color-content-secondary)", fontFamily: "var(--font-family-base)", marginTop: 2 }}>
              Configure package details, scope, pricing, and line items
            </div>
          </div>
          <button onClick={onClose} style={{ width: 32, height: 32, border: "1px solid var(--color-border-primary)", borderRadius: 8, background: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--color-content-secondary)" }}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M2 2l10 10M12 2L2 12" /></svg>
          </button>
        </div>

        {/* Drawer body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "16px 24px", display: "flex", flexDirection: "column", gap: 14 }}>

          {/* Name + description */}
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div>
              <label style={labelStyle}>Package Name *</label>
              <input style={inputStyle} value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Annual Maintenance Contract" autoFocus />
            </div>
            <div>
              <label style={labelStyle}>Description</label>
              <input style={inputStyle} value={description} onChange={e => setDescription(e.target.value)} placeholder="Short description shown in Browse Items" />
            </div>
          </div>

          <div style={{ height: 1, background: "var(--color-border-primary)" }} />

          {/* Package Type */}
          <div>
            <label style={labelStyle}>Package Type</label>
            <div style={{ position: "relative" }}>
              <select
                value={packageTypeId}
                onChange={e => setPackageTypeId(e.target.value)}
                style={{ ...inputStyle, cursor: "pointer", appearance: "none", paddingRight: 28 }}
              >
                {packageTypes.map(pt => (
                  <option key={pt.id} value={pt.id}>{pt.name}</option>
                ))}
              </select>
              <svg style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }} width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="#6B7280" strokeWidth="1.4" strokeLinecap="round"><path d="M2 3.5l3 3 3-3" /></svg>
            </div>
          </div>

          <div style={{ height: 1, background: "var(--color-border-primary)" }} />

          {/* Pricing */}
          <div>
            <label style={{ ...labelStyle, marginBottom: 12 }}>Package Price</label>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <label style={{ ...labelStyle, fontWeight: 500, color: "var(--color-content-secondary)" }}>Price (€)</label>
                <input
                  style={inputStyle} type="number" min="0" step="0.01"
                  value={price} onChange={e => setPrice(e.target.value)}
                  placeholder="0.00"
                />
              </div>
              <div>
                <label style={{ ...labelStyle, fontWeight: 500, color: "var(--color-content-secondary)" }}>Unit</label>
                <select
                  style={{ ...inputStyle, cursor: "pointer" }}
                  value={unit} onChange={e => setUnit(e.target.value)}
                >
                  {UNIT_OPTIONS.map(u => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
            </div>
          </div>

          <div style={{ height: 1, background: "var(--color-border-primary)" }} />

          {/* Line items */}
          <div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
              <div>
                <label style={labelStyle}>Line Items</label>
                <p style={{ margin: 0, fontSize: 12, color: "var(--color-content-secondary)", fontFamily: "var(--font-family-base)" }}>
                  Added automatically to the quotation / invoice when this package is selected.
                </p>
              </div>
            </div>

            {lineItems.length === 0 && (
              <div style={{ padding: "16px", border: "1px dashed var(--color-border-primary)", borderRadius: 8, textAlign: "center", fontSize: 12, color: "var(--color-content-secondary)", fontFamily: "var(--font-family-base)", marginBottom: 12 }}>
                No line items — the package price will be added as a single line item.
              </div>
            )}

            {lineItems.length > 0 && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 56px 80px 80px 28px", gap: 6, marginBottom: 4 }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: "var(--color-content-tertiary)", fontFamily: "var(--font-family-base)", paddingLeft: 2 }}>Name</span>
                <span style={{ fontSize: 11, fontWeight: 600, color: "var(--color-content-tertiary)", fontFamily: "var(--font-family-base)", textAlign: "center" }}>Qty</span>
                <span style={{ fontSize: 11, fontWeight: 600, color: "var(--color-content-tertiary)", fontFamily: "var(--font-family-base)" }}>Unit</span>
                <span style={{ fontSize: 11, fontWeight: 600, color: "var(--color-content-tertiary)", fontFamily: "var(--font-family-base)" }}>Price €</span>
                <span />
              </div>
            )}

            {lineItems.map((li, i) => (
              <div key={li.id} style={{ display: "grid", gridTemplateColumns: "1fr 56px 80px 80px 28px", gap: 6, alignItems: "center", marginBottom: 8 }}>
                <input
                  style={{ ...inputStyle, fontSize: 12 }}
                  value={li.name} onChange={e => updateLineItem(li.id, "name", e.target.value)}
                  placeholder={`Line item ${i + 1}`}
                />
                <input
                  style={{ ...inputStyle, fontSize: 12, textAlign: "center" }}
                  type="number" min="1" value={li.qty}
                  onChange={e => updateLineItem(li.id, "qty", parseFloat(e.target.value) || 1)}
                />
                <select
                  style={{ ...inputStyle, fontSize: 12, cursor: "pointer" }}
                  value={li.unit} onChange={e => updateLineItem(li.id, "unit", e.target.value)}
                >
                  {UNIT_OPTIONS.map(u => <option key={u} value={u}>{u}</option>)}
                </select>
                <input
                  style={{ ...inputStyle, fontSize: 12 }}
                  type="number" min="0" step="0.01" value={li.price}
                  onChange={e => updateLineItem(li.id, "price", parseFloat(e.target.value) || 0)}
                  placeholder="€ 0"
                />
                <button
                  onClick={() => removeLineItem(li.id)}
                  style={{ width: 28, height: 32, border: "1px solid var(--color-border-primary)", borderRadius: 6, background: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--color-content-tertiary)", flexShrink: 0 }}
                >
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M2 2l8 8M10 2L2 10" /></svg>
                </button>
              </div>
            ))}

            <button
              onClick={addLineItem}
              style={{ display: "flex", alignItems: "center", gap: 6, height: 32, padding: "0 12px", border: "1px dashed var(--color-border-primary)", borderRadius: 7, background: "transparent", color: "var(--color-content-secondary)", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "var(--font-family-base)" }}
            >
              <IcPlus /> Add line item
            </button>
          </div>
        </div>

        {/* Drawer footer */}
        <div style={{ padding: "16px 24px", borderTop: "1px solid var(--color-border-primary)", display: "flex", gap: 10, justifyContent: "flex-end", flexShrink: 0 }}>
          <button
            onClick={onClose}
            style={{ height: 36, padding: "0 16px", border: "1px solid var(--color-border-primary)", borderRadius: 8, background: "var(--color-bg-primary)", color: "var(--color-content-primary)", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "var(--font-family-base)" }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!name.trim()}
            style={{ height: 36, padding: "0 20px", border: "none", borderRadius: 8, background: name.trim() ? "var(--color-content-brand)" : "var(--color-bg-accent)", color: name.trim() ? "#fff" : "var(--color-content-tertiary)", fontSize: 13, fontWeight: 700, cursor: name.trim() ? "pointer" : "default", fontFamily: "var(--font-family-base)", transition: "all 100ms ease" }}
          >
            {isNew ? "Create Package" : "Save Changes"}
          </button>
        </div>
      </div>
    </>
  );
}

// ─── Finance: Price Book ──────────────────────────────────────────────────────

const PRICE_BOOK_CURRENCY_OPTIONS = [
  { value: "EUR", label: "EUR — Euro (€)" },
  { value: "USD", label: "USD — US Dollar ($)" },
  { value: "GBP", label: "GBP — British Pound (£)" },
  { value: "CHF", label: "CHF — Swiss Franc (Fr.)" },
  { value: "AED", label: "AED — UAE Dirham (د.إ)" },
  { value: "AUD", label: "AUD — Australian Dollar (A$)" },
  { value: "CAD", label: "CAD — Canadian Dollar (C$)" },
  { value: "CNY", label: "CNY — Chinese Yuan (¥)" },
  { value: "DKK", label: "DKK — Danish Krone (kr)" },
  { value: "HKD", label: "HKD — Hong Kong Dollar (HK$)" },
  { value: "INR", label: "INR — Indian Rupee (₹)" },
  { value: "JPY", label: "JPY — Japanese Yen (¥)" },
  { value: "MXN", label: "MXN — Mexican Peso (MX$)" },
  { value: "NOK", label: "NOK — Norwegian Krone (kr)" },
  { value: "NZD", label: "NZD — New Zealand Dollar (NZ$)" },
  { value: "PLN", label: "PLN — Polish Zloty (zł)" },
  { value: "SAR", label: "SAR — Saudi Riyal (SR)" },
  { value: "SEK", label: "SEK — Swedish Krona (kr)" },
  { value: "SGD", label: "SGD — Singapore Dollar (S$)" },
  { value: "TRY", label: "TRY — Turkish Lira (₺)" },
  { value: "ZAR", label: "ZAR — South African Rand (R)" },
];

function FinancePriceBookContent() {
  const { config, setConfig } = useFinanceConfig();
  const items = config.priceBooks;
  function setItems(updater: PriceBook[] | ((prev: PriceBook[]) => PriceBook[])) {
    const next = typeof updater === "function" ? updater(config.priceBooks) : updater;
    setConfig({ ...config, priceBooks: next });
  }
  const [mode, setMode] = React.useState<"add" | "edit" | null>(null);
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [formName, setFormName] = React.useState("");
  const [formDesc, setFormDesc] = React.useState("");
  const [formCurrency, setFormCurrency] = React.useState("EUR");

  function openAdd() {
    setFormName(""); setFormDesc(""); setFormCurrency("EUR");
    setEditingId(null); setMode("add");
  }

  function openEdit(item: PriceBook) {
    setFormName(item.name); setFormDesc(item.description); setFormCurrency(item.currency);
    setEditingId(item.id); setMode("edit");
  }

  function handleSave() {
    if (!formName.trim()) return;
    if (mode === "add") {
      setItems(prev => [...prev, { id: `pb-${Date.now()}`, name: formName.trim(), description: formDesc.trim(), currency: formCurrency }]);
    } else if (mode === "edit" && editingId) {
      setItems(prev => prev.map(i => i.id === editingId ? { ...i, name: formName.trim(), description: formDesc.trim(), currency: formCurrency } : i));
    }
    setMode(null); setEditingId(null);
  }

  function handleDelete(id: string) {
    setItems(prev => prev.filter(i => i.id !== id));
  }

  function handleCancel() {
    setMode(null); setEditingId(null);
  }

  const currencyLabel = (code: string) => PRICE_BOOK_CURRENCY_OPTIONS.find(o => o.value === code)?.label.split(" — ")[1] ?? code;

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: "24px 32px 48px" }}>
      <div style={{ maxWidth: 880, display: "flex", flexDirection: "column", gap: 28 }}>

        {/* Header */}
        <div>
          <h2 style={{ margin: "0 0 6px", fontSize: 16, fontWeight: 700, color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)", letterSpacing: "-0.32px", lineHeight: "24px" }}>
            Price Book
          </h2>
          <p style={{ margin: 0, fontSize: 14, fontWeight: 500, color: "var(--color-content-secondary)", fontFamily: "var(--font-family-base)", lineHeight: "20px" }}>
            Manage your catalogue of price books with standardised pricing for use in quotations and invoices.
          </p>
        </div>

        <div style={{ height: 1, background: "var(--color-border-primary)" }} />

        {/* Add button — only visible when not in add/edit mode */}
        {mode === null && (
          <button
            onClick={openAdd}
            style={{ display: "inline-flex", alignItems: "center", gap: 6, border: "none", background: "transparent", cursor: "pointer", padding: 0, fontSize: 13, fontWeight: 700, color: "var(--color-content-brand)", fontFamily: "var(--font-family-base)", alignSelf: "flex-start" }}
          >
            <IcPlus />
            Add New Price Book
          </button>
        )}

        {/* Inline create / edit form */}
        {mode !== null && (
          <div style={{ border: "1px solid var(--color-border-primary)", borderRadius: 10, padding: "20px 20px 16px", background: "var(--color-bg-primary)", display: "flex", flexDirection: "column", gap: 16 }}>
            <h3 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)", textTransform: "uppercase", letterSpacing: "0.04em" }}>
              {mode === "add" ? "New Price Book" : "Edit Price Book"}
            </h3>
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <FormField label="Name *" style={{ maxWidth: 520 }}>
                <TextInput value={formName} onChange={setFormName} placeholder="e.g. Standard Rate Card" />
              </FormField>
              <FormField label="Description" style={{ maxWidth: 520 }}>
                <TextInput value={formDesc} onChange={setFormDesc} placeholder="e.g. Default pricing for labour, parts, and travel" />
              </FormField>
              <FormField label="Currency" style={{ maxWidth: 280 }}>
                <SelectInput value={formCurrency} onChange={setFormCurrency} options={PRICE_BOOK_CURRENCY_OPTIONS} />
              </FormField>
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", paddingTop: 4 }}>
              <button
                onClick={handleCancel}
                style={{ height: 36, padding: "0 16px", border: "1px solid var(--color-border-primary)", borderRadius: 8, background: "var(--color-bg-primary)", fontSize: 13, fontWeight: 600, color: "var(--color-content-secondary)", cursor: "pointer", fontFamily: "var(--font-family-base)" }}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={!formName.trim()}
                style={{ height: 36, padding: "0 20px", border: "none", borderRadius: 8, background: formName.trim() ? "var(--color-content-brand)" : "var(--color-bg-accent)", color: formName.trim() ? "#fff" : "var(--color-content-tertiary)", fontSize: 13, fontWeight: 700, cursor: formName.trim() ? "pointer" : "default", fontFamily: "var(--font-family-base)", transition: "all 100ms ease" }}
              >
                {mode === "add" ? "Create Price Book" : "Save Changes"}
              </button>
            </div>
          </div>
        )}

        {/* Items list */}
        {items.length > 0 ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {items.map(item => (
              <div
                key={item.id}
                style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", border: "1px solid var(--color-border-primary)", borderRadius: 10, background: "var(--color-bg-primary)" }}
              >
                {/* Book icon */}
                <div style={{ width: 36, height: 36, borderRadius: 8, background: "#EEF0FF", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="#0517F8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 2h7l3 3v11a1 1 0 01-1 1H4a1 1 0 01-1-1V3a1 1 0 011-1z" />
                    <path d="M11 2v3h3" />
                    <path d="M6 8.5h6M6 11h4" />
                  </svg>
                </div>
                {/* Name + description */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)", letterSpacing: "-0.14px" }}>
                    {item.name}
                  </div>
                  {item.description && (
                    <div style={{ fontSize: 12, fontWeight: 500, color: "var(--color-content-secondary)", fontFamily: "var(--font-family-base)", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {item.description}
                    </div>
                  )}
                </div>
                {/* Currency badge */}
                <span style={{ fontSize: 11, fontWeight: 700, borderRadius: 5, padding: "3px 8px", background: "#EFF6FF", color: "#1D4ED8", fontFamily: "var(--font-family-base)", flexShrink: 0, whiteSpace: "nowrap" }}>
                  {item.currency} · {currencyLabel(item.currency)}
                </span>
                {/* Edit + Delete */}
                <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                  <button
                    onClick={() => openEdit(item)}
                    style={{ width: 32, height: 32, border: "1px solid var(--color-border-primary)", borderRadius: 6, background: "var(--color-bg-primary)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--color-content-secondary)" }}
                  >
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"><path d="M9.5 2.5l2 2L4 12H2v-2L9.5 2.5z" /></svg>
                  </button>
                  <button
                    onClick={() => handleDelete(item.id)}
                    style={{ width: 32, height: 32, border: "1px solid var(--color-border-primary)", borderRadius: 6, background: "var(--color-bg-primary)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--color-content-secondary)" }}
                  >
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3.5h10M5 3.5V2h4v1.5M5.5 6v4.5M8.5 6v4.5M3 3.5l.7 8.5h6.6l.7-8.5" /></svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          mode === null && (
            <div style={{ textAlign: "center", padding: "48px 0", color: "var(--color-content-secondary)", fontSize: 14, fontFamily: "var(--font-family-base)" }}>
              No price books yet — click <strong>Add New Price Book</strong> to create one.
            </div>
          )
        )}

      </div>
    </div>
  );
}

// ─── Finance: Package Types ───────────────────────────────────────────────────

function FinancePackageTypesContent() {
  const { config, setConfig } = useFinanceConfig();
  const types = config.packageTypes;

  function setTypes(updater: PackageType[] | ((prev: PackageType[]) => PackageType[])) {
    const next = typeof updater === "function" ? updater(config.packageTypes) : updater;
    setConfig({ ...config, packageTypes: next });
  }

  const [formName, setFormName] = React.useState("");
  const [showForm, setShowForm] = React.useState(false);

  function handleAdd() {
    if (!formName.trim()) return;
    setTypes(prev => [...prev, { id: `pt-${Date.now()}`, name: formName.trim(), isDefault: false }]);
    setFormName("");
    setShowForm(false);
  }

  const inputStyle: React.CSSProperties = {
    width: "100%", padding: "8px 10px", border: "1px solid var(--color-border-primary)",
    borderRadius: 7, background: "var(--color-bg-primary)", fontFamily: "var(--font-family-base)",
    fontSize: 13, color: "var(--color-content-primary)", outline: "none", boxSizing: "border-box",
  };

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: "24px 32px 48px" }}>
      <div style={{ maxWidth: 640, display: "flex", flexDirection: "column", gap: 24 }}>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16 }}>
          <div>
            <h2 style={{ margin: "0 0 6px", fontSize: 16, fontWeight: 700, color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)", letterSpacing: "-0.32px" }}>
              Package Types
            </h2>
            <p style={{ margin: 0, fontSize: 14, fontWeight: 500, color: "var(--color-content-secondary)", fontFamily: "var(--font-family-base)", lineHeight: "20px" }}>
              Define the types used to categorise service packages. Types appear as filter tabs in Browse Items.
            </p>
          </div>
          <button
            onClick={() => setShowForm(true)}
            style={{ display: "flex", alignItems: "center", gap: 6, height: 36, padding: "0 14px", border: "none", borderRadius: 8, background: "var(--color-content-brand)", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "var(--font-family-base)", flexShrink: 0 }}
          >
            <IcPlus /> Add Type
          </button>
        </div>

        <div style={{ height: 1, background: "var(--color-border-primary)" }} />

        {/* Type list */}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {types.map(pt => (
            <div key={pt.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", border: "1px solid var(--color-border-primary)", borderRadius: 8, background: "var(--color-bg-primary)" }}>
              <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)" }}>
                {pt.name}
              </span>
              {pt.isDefault ? (
                <span style={{ fontSize: 11, fontWeight: 700, borderRadius: 4, padding: "2px 8px", background: "#F3F4F6", color: "#748094", fontFamily: "var(--font-family-base)" }}>
                  Default
                </span>
              ) : (
                <button
                  onClick={() => setTypes(prev => prev.filter(t => t.id !== pt.id))}
                  style={{ height: 30, width: 30, border: "1px solid var(--color-border-primary)", borderRadius: 7, background: "var(--color-bg-primary)", color: "var(--color-content-secondary)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                >
                  <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"><path d="M2 4h10M5 4V3a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v1M5.5 6.5v4M8.5 6.5v4M3.5 4l.5 7.5a1 1 0 0 0 1 .5h4a1 1 0 0 0 1-.5L11 4" /></svg>
                </button>
              )}
            </div>
          ))}

          {/* Inline add form */}
          {showForm && (
            <div style={{ padding: "12px 14px", border: "1px dashed var(--color-border-primary)", borderRadius: 8, display: "flex", gap: 8 }}>
              <input
                autoFocus
                value={formName}
                onChange={e => setFormName(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") handleAdd(); if (e.key === "Escape") { setShowForm(false); setFormName(""); } }}
                placeholder="e.g. Inspection"
                style={{ ...inputStyle, flex: 1 }}
              />
              <button onClick={handleAdd} disabled={!formName.trim()} style={{ height: 34, padding: "0 14px", border: "none", borderRadius: 7, background: formName.trim() ? "var(--color-content-brand)" : "#E5E7EB", color: formName.trim() ? "#fff" : "#9CA3AF", fontSize: 12, fontWeight: 700, cursor: formName.trim() ? "pointer" : "not-allowed", fontFamily: "var(--font-family-base)" }}>
                Add
              </button>
              <button onClick={() => { setShowForm(false); setFormName(""); }} style={{ height: 34, padding: "0 12px", border: "1px solid var(--color-border-primary)", borderRadius: 7, background: "var(--color-bg-primary)", color: "var(--color-content-secondary)", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "var(--font-family-base)" }}>
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Section content map ──────────────────────────────────────────────────────

const SECTION_META: Record<SettingsSection, { title: string; description: string }> = {
  general: { title: "General", description: "" },
  "users-teams": { title: "Users & Teams", description: "Manage team members, roles, and group permissions across your workspace." },
  "access-permissions": { title: "Access & Permissions", description: "Control what each role can see and do within the platform." },
  "asset-config": { title: "Asset Configuration", description: "Define asset types and their attributes for your inventory." },
  "custom-fields-asset": { title: "Custom Fields — Assets", description: "Add custom data fields to capture additional asset information." },
  "import-data-asset": { title: "Import Data — Assets", description: "Bulk-import asset records from CSV or Excel files." },
  "custom-fields-work": { title: "Custom Fields — Work Orders", description: "Add custom data fields to your work orders for richer reporting." },
  "import-data-work": { title: "Import Data — Work", description: "Bulk-import work order records." },
  "resource-management": { title: "Resource Management", description: "Configure technicians, teams, and availability for scheduling." },
  "time-tracker-tags": { title: "Time Tracker Tags", description: "Create and manage tags used to categorise time tracking entries." },
  "work-order-types": { title: "Work Order Types", description: "Define work order categories like Corrective, Preventive, or Inspection." },
  "work-order-statuses": { title: "Work Order Statuses", description: "Customise the status pipeline for your work order workflow." },
  "sla-config": { title: "SLA Configuration", description: "Define SLA agreements, targets, applicable features, and start / pause / cancel conditions." },
  "notifications-work": { title: "Notifications", description: "Configure which events trigger notifications for your team." },
  "email-config": { title: "Email Configuration", description: "Set up outbound email settings, sender names, and reply-to addresses." },
  "finance-invoice-config": { title: "Quotation & Invoice", description: "Configure sender details, tax information, and defaults for quotations and invoices." },
  "finance-branding": { title: "Branding", description: "Upload header and footer images used on all quotations and invoices." },
  "finance-service-packages": { title: "Service Packages", description: "Define reusable service packages with pricing and line items for use in quotations and invoices." },
  "finance-price-book": { title: "Price Book", description: "Manage your catalogue of parts, labour rates, and services with standardised pricing for use in quotations and invoices." },
  "finance-package-types": { title: "Package Types", description: "Define types used to categorise service packages and filter Browse Items." },
  "custom-fields-company": { title: "Custom Fields — Companies", description: "Add extra fields to company records to capture business-specific data." },
  "import-data-company": { title: "Import Data — Companies", description: "Bulk-import company and contact records from CSV files." },
  branding: { title: "Branding", description: "Upload your logo, set brand colours, and customise the customer portal appearance." },
  "notifications-portal": { title: "Notifications — Portal", description: "Control which notifications customers receive via the portal." },
  profile: { title: "Profile", description: "Update your personal information, avatar, and display preferences." },
  "notifications-personal": { title: "Notifications", description: "Choose which platform events you receive personal notifications for." },
  security: { title: "Security", description: "Manage your password, two-factor authentication, and active sessions." },
};

// ─── Main Settings Page ───────────────────────────────────────────────────────

export function SettingsPage() {
  const [activeSection, setActiveSection] = React.useState<SettingsSection>("general");

  function nav(section: SettingsSection) {
    return <NavItem
      key={section}
      icon={ICON_MAP[section]}
      label={SECTION_META[section].title}
      active={activeSection === section}
      onClick={() => setActiveSection(section)}
    />;
  }

  function renderContent() {
    if (activeSection === "general") return <GeneralContent />;
    if (activeSection === "email-config") return <EmailConfigContent />;
    if (activeSection === "finance-invoice-config") return <FinanceInvoiceConfigContent />;
    if (activeSection === "finance-branding") return <FinanceBrandingContent />;
    if (activeSection === "finance-service-packages") return <FinanceServicePackagesContent />;
    if (activeSection === "finance-price-book") return <FinancePriceBookContent />;
    if (activeSection === "finance-package-types") return <FinancePackageTypesContent />;
    if (activeSection === "sla-config") return <SLAConfigContent />;
    const meta = SECTION_META[activeSection];
    return <PlaceholderContent title={meta.title} description={meta.description} />;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden", background: "var(--color-bg-primary)" }}>
      <PageHeader breadcrumbs={[{ label: "Settings" }]} />

      <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>

        {/* ── Secondary navigation ──────────────────────────────────────── */}
        <div style={{
          width: 224,
          flexShrink: 0,
          borderRight: "1px solid var(--color-border-primary)",
          overflowY: "auto",
          padding: "24px 16px 24px 24px",
          display: "flex",
          flexDirection: "column",
          gap: 24,
        }}>
          <NavGroup title="Workspace">
            {nav("general")}
            {nav("users-teams")}
            {nav("access-permissions")}
          </NavGroup>

          <NavGroup title="Asset Management">
            {nav("asset-config")}
            {nav("custom-fields-asset")}
            {nav("import-data-asset")}
          </NavGroup>

          <NavGroup title="Work Management">
            {nav("custom-fields-work")}
            {nav("resource-management")}
            {nav("time-tracker-tags")}
            {nav("work-order-types")}
            {nav("work-order-statuses")}
            {nav("sla-config")}
            {nav("notifications-work")}
            {nav("email-config")}
          </NavGroup>

          <NavGroup title="Company Management">
            {nav("custom-fields-company")}
            {nav("import-data-company")}
          </NavGroup>

          <NavGroup title="Finance">
            {nav("finance-invoice-config")}
            {nav("finance-branding")}
            {nav("finance-service-packages")}
            {nav("finance-price-book")}
            {nav("finance-package-types")}
          </NavGroup>

          <NavGroup title="Customer Portal">
            {nav("branding")}
            {nav("notifications-portal")}
          </NavGroup>

          <NavGroup title="Personal">
            {nav("profile")}
            {nav("notifications-personal")}
            {nav("security")}
          </NavGroup>
        </div>

        {/* ── Content area ──────────────────────────────────────────────── */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          {renderContent()}
        </div>
      </div>
    </div>
  );
}

// ─── Icon map ─────────────────────────────────────────────────────────────────

const ICON_MAP: Record<SettingsSection, React.ReactNode> = {
  general: <IcGeneral />,
  "users-teams": <IcUsers />,
  "access-permissions": <IcAccess />,
  "asset-config": <IcAsset />,
  "custom-fields-asset": <IcCustomField />,
  "import-data-asset": <IcUpload />,
  "custom-fields-work": <IcCustomField />,
  "import-data-work": <IcUpload />,
  "resource-management": <IcResource />,
  "time-tracker-tags": <IcClock />,
  "work-order-types": <IcWorkOrderType />,
  "work-order-statuses": <IcStatus />,
  "sla-config": <IcSLA />,
  "notifications-work": <IcBell />,
  "email-config": <IcMail />,
  "finance-invoice-config": <IcInvoice />,
  "finance-branding": <IcFinanceBranding />,
  "finance-service-packages": <IcServicePackages />,
  "finance-price-book": <IcPriceBook />,
  "finance-package-types": <IcServicePackages />,
  "custom-fields-company": <IcCustomField />,
  "import-data-company": <IcUpload />,
  branding: <IcBranding />,
  "notifications-portal": <IcBell />,
  profile: <IcProfile />,
  "notifications-personal": <IcBell />,
  security: <IcLock />,
};
